import React, { useState, useEffect } from "react";
import { Select } from "antd";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { appConfig } from "../../config/app";
import Loading from "../../app/ui/Loading/loading";
import { downloadReceipt, formatPaymentDataForReceipt } from "../../utils/receiptGenerator";

const { Option } = Select;

const PaymentPreview = () => {
  const [player, setPlayer] = useState(null);
  const [membershipOptions, setMembershipOptions] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showPaymentMethodSelection, setShowPaymentMethodSelection] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    window.location.protocol === 'https:' ? 'apple-pay' : null
  );
  const [checkoutId, setCheckoutId] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [resendingSms, setResendingSms] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [isPaymentExpired, setIsPaymentExpired] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [hasMembershipChanged, setHasMembershipChanged] = useState(false);
  const [originalMembershipId, setOriginalMembershipId] = useState(null);
  const [hiddenDiscount, setHiddenDiscount] = useState(null); // For discounts that apply but aren't shown in UI
  const [companySettings, setCompanySettings] = useState(null); // Company settings for VAT and other configurations
  const [paymentProvider, setPaymentProvider] = useState('HyperPay'); // Payment provider (HyperPay or MyFatoorah)
  const [myFatoorahPaymentMethods, setMyFatoorahPaymentMethods] = useState([]); // Available MyFatoorah payment methods
  const [selectedMyFatoorahMethod, setSelectedMyFatoorahMethod] = useState(null); // Selected MyFatoorah payment method
  const [currencyCode, setCurrencyCode] = useState('SAR'); // Company's currency code (default to SAR)
  const [termsAccepted, setTermsAccepted] = useState(false); // Terms and conditions acceptance
  const [promoCode, setPromoCode] = useState(''); // Promo code input
  const [isApplyingPromo, setIsApplyingPromo] = useState(false); // Loading state for promo apply
  const [promoError, setPromoError] = useState(''); // Error message for promo code
  const [promoApplied, setPromoApplied] = useState(false); // Whether a promo was successfully applied
  const [isAllowManualMembershipSelection, setIsAllowManualMembershipSelection] = useState(true); // Flag to control membership selection
  const [paymentType, setPaymentType] = useState(null); // Payment type (Product, Membership, etc.)
  const [productPrice, setProductPrice] = useState(null); // Product price (subtotal without VAT)
  const [productVat, setProductVat] = useState(null); // Product VAT amount
  const [productVatPercentage, setProductVatPercentage] = useState(null); // Product VAT percentage
  const [productItems, setProductItems] = useState([]); // Product invoice items
  const [applyWalletPoints, setApplyWalletPoints] = useState(false); // Wallet points checkbox state

  // Get GUID from URL query parameters
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paymentReferenceNo = queryParams.get("ref");

  // Handle resend SMS function
  const handleResendSms = async () => {
    try {
      setResendingSms(true);
      setResendMessage(""); // Clear any previous message

      const response = await axios.post(
        `${appConfig.DotNet_Api_Endpoint}/Player/Renew/resend-payment-sms`,
        {
          paymentCode: paymentReferenceNo
        }
      );

      if (response.data && response.data.data && response.data.data.redirectUrl) {
        // Show success message in UI instead of alert
        setResendMessage("We have sent new link to your phone please check SMS");

        // Future implementation: Automatically redirect to new payment link
        // window.location.href = response.data.data.redirectUrl;
      } else {
        setResendMessage("We have sent new link to your phone please check SMS");
      }
    } catch (err) {
      console.error('Error resending SMS:', err);

      // Check if it's a validation error (400 status code)
      if (err.response && err.response.status === 400 && err.response.data && err.response.data.message) {
        // Show the specific validation message from the backend
        setResendMessage(err.response.data.message);
      } else {
        // Generic error message for other errors
        setResendMessage("Failed to resend SMS. Please try again.");
      }
    } finally {
      setResendingSms(false);
    }
  };

  useEffect(() => {
    // Add Apple Pay script to head
    const addApplePayScript = () => {
      // Check if script already exists
      const existingScript = document.querySelector('script[data-apple-pay-options]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.setAttribute('data-apple-pay-options', 'true');
        script.innerHTML = `
          var wpwlOptions = {
            applePay: {
              version: 3,
              checkAvailability: "applePayCapabilities", //for the button to appear
              displayName: "MyStore",
              total: { 
                label: "COMPANY, INC.",
                amount: "0.10"
              },
              supportedNetworks: ["mada", "masterCard", "visa"],
              countryCode: "SA",
              buttonStyle: "black"
            }
          }
        `;
        document.head.appendChild(script);
      }
    };

    // Add discount card CSS
    const addDiscountCardCSS = () => {
      const existingCSS = document.querySelector('style[data-discount-card-css]');
      if (!existingCSS) {
        const customCSS = document.createElement('style');
        customCSS.setAttribute('data-discount-card-css', 'true');
        customCSS.innerHTML = `
          .discount-card {
            transition: all 0.2s ease !important;
            min-height: 120px;
            display: flex;
            flex-direction: column;
            border: 2px solid #3b82f6 !important;
            background-color: #eff6ff !important;
            color: #1e40af !important;
          }
          
          .discount-card:hover {
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
            transform: translateY(-2px) !important;
            border-color: #2563eb !important;
          }
          
          .discount-card.selected-discount {
            background-color: #040c4b !important;
            border-color: #040c4b !important;
            color: white !important;
            box-shadow: 0 4px 16px rgba(4, 12, 75, 0.3) !important;
          }
          
          .discount-card.selected-discount:hover {
            background-color: #030a42 !important;
            border-color: #030a42 !important;
            box-shadow: 0 6px 20px rgba(4, 12, 75, 0.4) !important;
          }
          
          .discount-card.selected-discount h6,
          .discount-card.selected-discount p,
          .discount-card.selected-discount div {
            color: white !important;
          }
          
          .discount-card h6 {
            color: #1e40af !important;
          }
          
          .discount-card p {
            color: #64748b !important;
          }
          
          .discount-card .discount-value {
            color: #059669 !important;
          }
          
          .discount-card.selected-discount .discount-value {
            color: #86efac !important;
          }
          
          .clear-discount-card {
            border: 2px solid #e5e7eb !important;
            background-color: #f9fafb !important;
            color: #6b7280 !important;
          }
          
          .clear-discount-card:hover {
            border-color: #dc3545 !important;
            background-color: #fef2f2 !important;
            color: #dc3545 !important;
            box-shadow: 0 4px 12px rgba(220, 53, 69, 0.2) !important;
            transform: translateY(-2px) !important;
          }
          
          .cursor-pointer {
            cursor: pointer !important;
          }
        `;
        document.head.appendChild(customCSS);
      }
    };

    // Add the Apple Pay script
    addApplePayScript();

    // Add discount card CSS
    addDiscountCardCSS();

    const fetchPaymentDetails = async () => {
      try {
        if (!paymentReferenceNo) {
          throw new Error("Invalid payment reference number.");
        }

        const paymentResponse = await axios.get(
          `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/payment_logs/byPaymentCode/${paymentReferenceNo}`
        );

        // Check if payment is already completed
        console.log('Checking completion status:', {
          hasMessage: !!paymentResponse.data.message,
          isCompleted: paymentResponse.data.message?.isCompleted,
          showReceipt: paymentResponse.data.message?.showReceipt,
          fullResponse: paymentResponse.data
        });

        if (paymentResponse.data && typeof paymentResponse.data === 'object' &&
          paymentResponse.data.message &&
          (paymentResponse.data.message.isCompleted || paymentResponse.data.message.showReceipt)) {
          setIsPaymentCompleted(true);

          // For completed payments, the data is in the .data property
          const paymentLog = paymentResponse.data.data;
          setPaymentDetails({
            playerId: paymentLog.playerId.toString(),
            membershipId: paymentLog.membershipId.toString(),
            team: paymentLog.team,
            session: paymentLog.session,
            ageGroup: paymentLog.ageGroup,
            startDate: paymentLog.startDate,
            branch: paymentLog.branch.toString(),
            comment: paymentLog.comment,
            invComment: paymentLog.invComment,
            invPaymentStatus: paymentLog.invPaymentStatus,
            invPaymentMethod: paymentLog.invPaymentMethod,
            invDiscount: parseFloat(paymentLog.invDiscount) || 0,
            addClothes: paymentLog.addClothes,
            onlineDiscountId: paymentLog.onlineDiscountId || null,
            discountPercent: parseInt(paymentLog.discountPercent) || 0,
            paymentLogGuid: paymentLog.paymentLogGuid,
            payment_code: paymentReferenceNo,
            ageGroupName: paymentLog.ageGroupName,
            teamName: paymentLog.teamName,
            sessionName: paymentLog.sessionName,
            branchName: paymentLog.branchName,
            membershipName: paymentLog.membershipName,
            // Add additional receipt details
            playerName: paymentLog.playerName,
            phoneNumber: paymentLog.phoneNumber,
            amount: paymentLog.amount,
            currency: paymentLog.currency,
            transactionDate: paymentLog.transactionDate,
            status: paymentLog.status,
            referenceNumber: paymentLog.referenceNumber,
            paymentMethod: paymentLog.paymentMethod,
            expiryDate: paymentLog.expiryDate,
            bundleAmount: paymentLog.bundleAmount || 0,
            paymentType: paymentLog.paymentType,
            pointsUsed: paymentLog.pointsUsed || 0, // Add pointsUsed for receipts
            source: paymentLog.source || null,
          });
          setPaymentType(paymentLog.paymentType);
          setProductPrice(paymentLog.productPrice);
          setProductVat(paymentLog.productVat);
          setProductVatPercentage(paymentLog.productVatPercentage);
          setProductItems(paymentLog.productItems || []);
          setLoading(false);
          console.log('Payment already completed, showing receipt. isPaymentCompleted:', true);
          console.log('Payment details set for completed payment:', {
            paymentCode: paymentReferenceNo,
            playerName: paymentLog.playerName,
            amount: paymentLog.amount
          });
          return; // Exit early for completed payments
        }

        // For non-completed payments, data is directly in paymentResponse.data
        const paymentLog = paymentResponse.data;

        // Check if payment is expired
        if (paymentLog.status === "expired") {
          setIsPaymentExpired(true);
          setError("This payment link has expired. Please use the resend link to get a new payment link.");
          setLoading(false);
          return;
        }

        setPaymentDetails({
          playerId: paymentLog.playerId.toString(),
          membershipId: paymentLog.membershipId.toString(),
          team: paymentLog.team,
          session: paymentLog.session,
          ageGroup: paymentLog.ageGroup,
          startDate: paymentLog.startDate,
          branch: paymentLog.branch.toString(),
          comment: paymentLog.comment,
          invComment: paymentLog.invComment,
          invPaymentStatus: paymentLog.invPaymentStatus,
          invPaymentMethod: paymentLog.invPaymentMethod,
          invDiscount: parseFloat(paymentLog.invDiscount) || 0,
          addClothes: paymentLog.addClothes,
          onlineDiscountId: paymentLog.onlineDiscountId || null,
          discountPercent: parseInt(paymentLog.discountPercent) || 0,
          paymentLogGuid: paymentLog.paymentLogGuid,
          payment_code: paymentReferenceNo,
          bundleAmount: paymentLog.bundleAmount || 0,
          ageGroupName: paymentLog.ageGroupName,
          teamName: paymentLog.teamName,
          sessionName: paymentLog.sessionName,
          branchName: paymentLog.branchName,
          membershipName: paymentLog.membershipName,
          paymentType: paymentLog.paymentType,
          amount: paymentLog.amount || 0, // Add amount for Transfer type
          pointsUsed: paymentLog.pointsUsed || 0, // Add pointsUsed for Transfer type
          source: paymentLog.source || null,
        });
        setPaymentType(paymentLog.paymentType);
        setProductPrice(paymentLog.productPrice);
        setProductVat(paymentLog.productVat);
        setProductVatPercentage(paymentLog.productVatPercentage);
        setProductItems(paymentLog.productItems || []);
        console.log('[ProductItems] from API:', paymentLog.productItems);
      } catch (err) {
        // Check if the error has a specific message from the backend
        let errorMessage = "Failed to load payment details.";
        let isExpired = false;

        if (err.response && err.response.data) {
          // Handle different response structures
          if (err.response.data.message) {
            if (typeof err.response.data.message === 'object' && err.response.data.message.message) {
              errorMessage = err.response.data.message.message;
            } else if (typeof err.response.data.message === 'string') {
              errorMessage = err.response.data.message;
            }
          } else if (typeof err.response.data === 'string') {
            errorMessage = err.response.data;
          }

          // Check if the error message indicates expiry
          if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('rexpired')) {
            isExpired = true;
          }
        }

        if (isExpired) {
          setIsPaymentExpired(true);
          setError(errorMessage);
        } else {
          setError(errorMessage);
        }
        setLoading(false); // Stop loading so error can be displayed
        console.error('Payment details fetch error:', {
          error: err,
          response: err.response?.data,
          status: err.response?.status,
          message: errorMessage,
          isExpired: isExpired
        });
      }
    };

    fetchPaymentDetails();
  }, [paymentReferenceNo]);

  console.log(paymentDetails);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!paymentDetails.playerId || isPaymentCompleted) return;

        setLoading(true);

        const playerResponse = await axios.get(
          `${appConfig.DotNet_Api_Endpoint}/soccer/user/players/${paymentDetails.playerId}`
        );
        const playerData = playerResponse.data;

        // Updated: Use membership API to get branch-specific pricing
        const branchId = paymentDetails.branch || playerData.currentMembership?.branchId || 10;

        console.log('Fetching membership data for branch:', branchId);

        // Fetch branch company ID from public endpoint (no auth required)
        let companyId = null;
        try {
          const branchCompanyResponse = await axios.get(
            `${appConfig.DotNet_Api_Endpoint}/Branch/public/${branchId}/company`
          );
          companyId = branchCompanyResponse.data.companyId;
          const provider = branchCompanyResponse.data.paymentProvider || 'HyperPay';
          setPaymentProvider(provider);
          console.log('Company ID from branch:', companyId);
          console.log('Payment Provider:', provider);
        } catch (branchErr) {
          console.warn('Failed to fetch branch company ID:', branchErr);
          // Try to get from player data as fallback
          companyId = playerData.currentMembership?.branch?.companyId ||
            playerData.branch?.companyId;
          setPaymentProvider('HyperPay'); // Default to HyperPay on error
        }

        // Fetch membership data
        const membershipResponse = await axios.get(
          `${appConfig.DotNet_Api_Endpoint}/membership/branch/${branchId}/online`
        );
        const membershipsData = membershipResponse.data;

        if (!Array.isArray(membershipsData) || membershipsData.length === 0) {
          throw new Error(`No membership data available for branch ${branchId}`);
        }

        // If still no company ID, try from membership data
        if (!companyId) {
          companyId = membershipsData[0]?.branches?.find(b => b.branchId === parseInt(branchId))?.companyId;
        }

        // Fetch company settings if we have a company ID
        if (companyId) {
          try {
            const companyResponse = await axios.get(
              `${appConfig.DotNet_Api_Endpoint}/Company/${companyId}`
            );
            setCompanySettings(companyResponse.data);
            setCurrencyCode(companyResponse.data.currencyCode || 'SAR');

            // Set the membership selection flag (default to true if not set)
            // Also disable for Transfer payment type - membership is already selected at destination branch
            const allowMembershipSelection = companyResponse.data.isAllowManualMembershipSelection !== false
              && paymentType !== 'Transfer';
            setIsAllowManualMembershipSelection(allowMembershipSelection);

            console.log('Company settings loaded:', companyResponse.data);
            console.log('Currency code set to:', companyResponse.data.currencyCode);
            console.log('Allow manual membership selection:', allowMembershipSelection);
            console.log('Payment type:', paymentType);
          } catch (companyErr) {
            console.error('Failed to load company settings:', companyErr);
            // Continue without company settings - Tax will be enabled by default
          }
        } else {
          console.warn('No company ID found, tax will be enabled by default');
        }

        const options = membershipsData
          .filter(membership => {
            // Only include active memberships that have this branch or have a baseAmount
            return membership.isActive &&
              (membership.branches.some(branch => branch.branchId === parseInt(branchId)) ||
                membership.baseAmount > 0);
          })
          .map((membership) => {
            // Find the price for the current branch
            const branchPrice = membership.branches.find(branch => branch.branchId === parseInt(branchId));
            // Use branch-specific price if available, otherwise fall back to baseAmount
            const price = branchPrice && branchPrice.membershipAmount > 0
              ? branchPrice.membershipAmount
              : membership.baseAmount;

            return {
              value: membership.membershipId.toString(),
              label: membership.membershipName,
              localizedLabel: membership.localizedMembershipName,
              price: parseFloat(price) || 0,
              description: membership.description,
            };
          });

        console.log('Branch-specific membership options:', {
          branchId: branchId,
          options: options,
          rawMembershipsData: membershipsData
        });

        setMembershipOptions(options);

        // default membership
        const defaultMembership =
          options.find(
            (option) => option.value === paymentDetails.membershipId
          ) || options[0];
        setSelectedMembership(defaultMembership);

        // Store the original membership ID to track when user returns to it
        if (defaultMembership) {
          setOriginalMembershipId(defaultMembership.value);
        }

        // Fetch discounts for the default membership
        if (defaultMembership) {
          await fetchDiscountsForMembership(defaultMembership.value, branchId);
        }

        setPlayer(playerData);
      } catch (err) {
        setError("Failed to load player or membership data.");
        console.error('Error fetching data:', {
          error: err,
          response: err.response?.data,
          status: err.response?.status,
          branchId: paymentDetails.branch || 10
        });
      } finally {
        setLoading(false);
      }
    };

    if (paymentDetails.playerId && !isPaymentCompleted) {
      fetchData();
    }
  }, [paymentDetails, isPaymentCompleted]);

  // Auto-create Apple Pay widget when component loads and Apple Pay is default
  useEffect(() => {
    if (selectedPaymentMethod === 'apple-pay' && showPaymentMethodSelection && checkoutId) {
      setTimeout(() => {
        createApplePayWidgetInline();
      }, 100);
    }
  }, [selectedPaymentMethod, showPaymentMethodSelection, checkoutId]);

  // Function to fetch discounts for a specific membership and branch
  const fetchDiscountsForMembership = async (membershipId, branchId) => {
    try {
      const discountResponse = await axios.get(
        `${appConfig.DotNet_Api_Endpoint}/discount/online/membership/${membershipId}/branch/${branchId}`
      );

      const discountsData = discountResponse.data;

      if (Array.isArray(discountsData) && discountsData.length > 0) {
        const discountOptions = discountsData.map((discount) => ({
          value: discount.discountId.toString(),
          label: discount.name,
          description: discount.description,
          type: discount.type, // "percentage" or "fixed"
          discountValue: discount.value, // renamed to avoid conflict with 'value' property
          code: discount.code
        }));

        setDiscountOptions(discountOptions);

        // Check if there's a pre-selected discount in payment details and if it's still valid for this membership
        const preSelectedDiscount = discountOptions.find(
          option => option.value === paymentDetails.onlineDiscountId?.toString()
        );

        // Only keep the discount if it's available for this membership
        if (preSelectedDiscount) {
          setSelectedDiscount(preSelectedDiscount);
          setHiddenDiscount(null); // Clear hidden discount if we found it in options
          console.log('Pre-selected discount is valid for this membership:', preSelectedDiscount);
        } else {
          // Clear the discount if it was set but not available for this membership
          setSelectedDiscount(null);
          if (paymentDetails.onlineDiscountId) {
            console.warn('Previously selected discount (ID:', paymentDetails.onlineDiscountId, ') is not available for this membership, fetching as hidden discount');
            // Fetch the hidden discount details and validate if it applies to this membership
            await fetchHiddenDiscount(paymentDetails.onlineDiscountId, membershipId);
          }
        }

        console.log('Discounts loaded for membership:', {
          membershipId,
          branchId,
          discountOptions
        });
      } else {
        setDiscountOptions([]);
        setSelectedDiscount(null);
        // Check if there's a hidden discount to apply
        if (paymentDetails.onlineDiscountId) {
          console.log('No online discounts available, but hidden discount exists:', paymentDetails.onlineDiscountId);
          await fetchHiddenDiscount(paymentDetails.onlineDiscountId, membershipId);
        }
      }
    } catch (err) {
      console.error('Error fetching discounts:', err);
      setDiscountOptions([]);
      setSelectedDiscount(null);
      // Still try to fetch hidden discount if it exists
      if (paymentDetails.onlineDiscountId) {
        await fetchHiddenDiscount(paymentDetails.onlineDiscountId, membershipId);
      }
    }
  };

  // Function to fetch a hidden discount (one that's not available for online but was pre-applied)
  const fetchHiddenDiscount = async (discountId, membershipId) => {
    try {
      const response = await axios.get(
        `${appConfig.DotNet_Api_Endpoint}/discount/${discountId}`
      );

      const discount = response.data;

      if (discount && discount.discountId) {
        // Check if discount applies to the current membership
        const appliesToMembership = discount.membershipScope === 'all' ||
          (discount.membershipScope === 'selected' &&
            discount.membershipIds &&
            discount.membershipIds.includes(parseInt(membershipId)));

        if (appliesToMembership) {
          const hiddenDiscountData = {
            value: discount.discountId.toString(),
            label: discount.name,
            description: discount.description,
            type: discount.type,
            discountValue: discount.value,
            code: discount.code,
            membershipScope: discount.membershipScope,
            membershipIds: discount.membershipIds,
            isHidden: true // Mark as hidden so we know not to show in UI
          };

          setHiddenDiscount(hiddenDiscountData);
          console.log('Hidden discount fetched and applies to membership:', hiddenDiscountData);
        } else {
          console.log('Hidden discount does not apply to this membership (scope:', discount.membershipScope, ', membershipIds:', discount.membershipIds, ')');
          setHiddenDiscount(null);
        }
      }
    } catch (err) {
      console.error('Error fetching hidden discount:', err);
      setHiddenDiscount(null);
    }
  };

  const handleMembershipChange = (value) => {
    const newMembership =
      membershipOptions.find((option) => option.value === value) ||
      membershipOptions[0];
    setSelectedMembership(newMembership);

    // Clear selected discount when changing membership
    setSelectedDiscount(null);

    // Check if user selected the original membership
    if (value === originalMembershipId) {
      // User returned to original membership - hide discount selection UI
      setHasMembershipChanged(false);
    } else {
      // User changed to a different membership - show discount selection UI
      setHasMembershipChanged(true);
      // Clear hidden discount when changing away from original membership
      // It will be re-fetched if applicable for the new membership
      setHiddenDiscount(null);
    }

    // Fetch discounts for the new membership (this will also validate hidden discount)
    if (newMembership && paymentDetails.branch) {
      fetchDiscountsForMembership(newMembership.value, paymentDetails.branch);
    }
  };

  const handleDiscountChange = (value) => {
    const newDiscount = value
      ? discountOptions.find((option) => option.value === value)
      : null;
    setSelectedDiscount(newDiscount);
  };

  // Clean up any existing HyperPay scripts
  const cleanupHyperPayScripts = () => {
    const existingScript = document.querySelector('script[src*="paymentWidgets.js"]');
    if (existingScript) {
      existingScript.remove();
    }

    const existingOptionsScript = document.querySelector('script[data-wpwl-options]');
    if (existingOptionsScript) {
      existingOptionsScript.remove();
    }

    const customCSS = document.querySelector('style[data-hyperpay-custom]');
    if (customCSS) {
      customCSS.remove();
    }

    const applePayCSS = document.querySelector('style[data-apple-pay-inline-css]');
    if (applePayCSS) {
      applePayCSS.remove();
    }
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (method) => {
    console.log('Payment method selected:', method);
    setSelectedPaymentMethod(method);

    if (method === 'apple-pay') {
      // For Apple Pay, immediately show the payment interface without intermediate steps
      setShowPaymentMethodSelection(false);
      setShowPaymentForm(true);
      setWidgetLoading(true);

      // Create Apple Pay form immediately
      setTimeout(() => {
        createApplePayForm();
      }, 100); // Reduced delay for faster response
    } else if (method === 'visa' || method === 'mastercard' || method === 'mada') {
      // For card payments, hide selection and show form
      setShowPaymentMethodSelection(false);
      setWidgetLoading(true);
      setShowPaymentForm(true);

      // Add a small delay to show the loading state, then create the form
      setTimeout(() => {
        createCardPaymentForm(method);
      }, 300);
    } else if (method === 'tamara') {
      // For Tamara, create a dedicated checkout with Tamara-specific params
      setShowPaymentMethodSelection(false);
      setWidgetLoading(true);
      setShowPaymentForm(true);

      setTimeout(() => {
        createTamaraPaymentForm();
      }, 300);
    } else if (method === 'tabby') {
      // For Tabby, create a dedicated checkout with Tabby-specific params
      setShowPaymentMethodSelection(false);
      setWidgetLoading(true);
      setShowPaymentForm(true);

      setTimeout(() => {
        createTabbyPaymentForm();
      }, 300);
    }
  };

  // Create inline Apple Pay widget for payment method selection
  const createApplePayWidgetInline = () => {
    console.log('Creating inline Apple Pay widget');

    try {
      // Clean up any existing scripts first
      cleanupHyperPayScripts();

      // Calculate total amount
      const finalAmount = calculateTotalAmount();

      // Build line items based on payment type
      let lineItemsConfig = '';

      if (paymentType === 'Product') {
        // For Product payments, show product price and VAT
        const productVatLineItem = productVat > 0 ? `,
              {
                label: "VAT (${(productVatPercentage || 0).toFixed(1)}%)",
                amount: "${(productVat || 0).toFixed(2)}"
              }` : '';

        lineItemsConfig = `[
              {
                label: "Product Price",
                amount: "${(productPrice || 0).toFixed(2)}"
              }${productVatLineItem}
            ]`;
      } else if (paymentType === 'Transfer') {
        // One line: the stored amount (discount, VAT and points already applied), equal to the total.
        lineItemsConfig = `[
              {
                label: "Branch Transfer",
                amount: "${finalAmount.toFixed(2)}"
              }
            ]`;
      } else {
        // For Membership payments
        let membershipAmount = selectedMembership?.price || 0;

        // Apply selected discount
        let discountAmount = 0;
        if (selectedDiscount) {
          if (selectedDiscount.type === 'percentage') {
            discountAmount = membershipAmount * (selectedDiscount.discountValue / 100);
            membershipAmount = membershipAmount * (1 - selectedDiscount.discountValue / 100);
          } else if (selectedDiscount.type === 'fixed') {
            discountAmount = Math.min(membershipAmount, selectedDiscount.discountValue);
            membershipAmount = Math.max(0, membershipAmount - selectedDiscount.discountValue);
          }
        }

        let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
        let subtotal = membershipAmount + bundleCost;

        // Check if VAT is enabled for this company
        const isTaxEnabled = companySettings?.isTaxEnabled !== false;
        const vatLineItem = isTaxEnabled ? `,
              {
                label: "VAT (15%)",
                amount: "${(subtotal * 0.15).toFixed(2)}"
              }` : '';

        const baseAmount = selectedMembership?.price || 0;
        const discountLineItem = selectedDiscount ? `,
              {
                label: "${selectedDiscount.label}${selectedDiscount.type === 'percentage' ? ` (${selectedDiscount.discountValue}%)` : ''}",
                amount: "-${discountAmount.toFixed(2)}"
              }` : '';

        lineItemsConfig = `[
              {
                label: "Membership Fee",
                amount: "${baseAmount.toFixed(2)}"
              }${discountLineItem}${paymentDetails.addClothes && paymentDetails.bundleAmount ? `,
              {
                label: "Bundle",
                amount: "${bundleCost.toFixed(2)}"
              }` : ''}${vatLineItem}
            ]`;
      }

      // Clear and create form container for inline Apple Pay
      const container = document.querySelector('.apple-pay-inline-wrapper');
      if (container) {
        container.innerHTML = '';

        const form = document.createElement('form');
        form.className = 'paymentWidgets';
        form.action = `${window.location.origin}/payment-callback.html`;
        form.setAttribute('data-brands', 'APPLEPAY');

        container.appendChild(form);
      }

      // Create HyperPay options with Apple Pay configuration
      const optionsScript = document.createElement('script');
      optionsScript.setAttribute('data-wpwl-options', 'true');
      const applePayInlineIntegrity = sessionStorage.getItem('hyperPayIntegrity') || '';
      // merchantIdentifier: "8ac7a4c79483092601948366b9d1011b", --test
      optionsScript.innerHTML = `
        var wpwlOptions = {
          ${applePayInlineIntegrity ? `integrity: "${applePayInlineIntegrity}",` : ''}
          paymentTarget: "_top",
          style: "plain",
          locale: "en",
          applePay: {
            version: 3,
            checkAvailability: "applePayCapabilities",
            merchantIdentifier: "8acda4cd9836ada901987a5594615b2d",
            displayName: "Al Hilal Academy",
            total: { 
              label: "AL HILAL ACADEMY",
              amount: "${finalAmount.toFixed(2)}",
              type: "final"
            },
            currencyCode: "${currencyCode}",
            countryCode: "${currencyCode === 'KWD' ? 'KW' : 'SA'}",
            supportedNetworks: ["mada", "masterCard", "visa", "amex"],
            merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
            buttonStyle: "black",
            buttonType: "pay",
            buttonSource: "js",
            lineItems: ${lineItemsConfig},
            onPaymentAuthorized: function(payment) {
              console.log('Apple Pay payment authorized:', payment);
              return {
                status: "SUCCESS"
              };
            },
            onCancel: function() {
              console.log('Apple Pay cancelled by user');
            },
            onPaymentMethodSelected: function(paymentMethod) {
              console.log('Apple Pay method selected:', paymentMethod);
              return {
                newTotal: {
                  label: "AL HILAL ACADEMY",
                  amount: "${finalAmount.toFixed(2)}",
                  type: "final"
                }
              };
            }
          },
          onReady: function() {
            console.log('HyperPay Apple Pay inline widget ready');
          },
          onError: function(error) {
            console.error('HyperPay Apple Pay inline error:', error);
            alert('Apple Pay error: ' + (error.message || 'Please try again'));
          }
        };
      `;
      document.head.appendChild(optionsScript);

      // Add custom CSS for Apple Pay button alignment
      const customCSS = document.createElement('style');
      customCSS.setAttribute('data-apple-pay-inline-css', 'true');
      customCSS.innerHTML = `
        .apple-pay-inline-wrapper .wpwl-container {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          background: transparent !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
        }
        
        .apple-pay-inline-wrapper .wpwl-form {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          background: transparent !important;
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
        }
        
        .apple-pay-inline-wrapper .wpwl-button {
          margin: 0 !important;
          border-radius: 8px !important;
          min-height: 40px !important;
          font-size: 16px !important;
          font-weight: 600 !important;
        }
        
        .apple-pay-inline-wrapper .wpwl-group {
          margin: 0 !important;
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
        }
      `;
      document.head.appendChild(customCSS);

      // Load HyperPay script
      const script = document.createElement('script');
      //for test
      // script.src = `https://eu-test.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
      script.src = `https://eu-prod.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
      script.async = true;

      script.onload = () => {
        console.log('HyperPay Apple Pay inline script loaded successfully');
      };

      script.onerror = () => {
        console.error('Failed to load HyperPay Apple Pay inline script');
        setError('Failed to load Apple Pay. Please try again.');
      };

      document.head.appendChild(script);

    } catch (error) {
      console.error('Apple Pay inline error:', error);
      setError('Apple Pay is not available. Please select a different payment method.');
    }
  };

  // Create Apple Pay payment form with HyperPay
  const createApplePayForm = () => {
    console.log('Creating Apple Pay payment form');

    try {
      // Clean up any existing scripts first
      cleanupHyperPayScripts();

      // Calculate total amount
      const finalAmount = calculateTotalAmount();

      // Build line items based on payment type
      let lineItemsConfig = '';

      if (paymentType === 'Product') {
        // For Product payments, show product price and VAT
        const productVatLineItem = productVat > 0 ? `,
            {
              label: "VAT (${(productVatPercentage || 0).toFixed(1)}%)",
              amount: "${(productVat || 0).toFixed(2)}"
            }` : '';

        lineItemsConfig = `[
            {
              label: "Product Price",
              amount: "${(productPrice || 0).toFixed(2)}"
            }${productVatLineItem}
          ]`;
      } else if (paymentType === 'Transfer') {
        // One line: the stored amount (discount, VAT and points already applied), equal to the total.
        lineItemsConfig = `[
            {
              label: "Branch Transfer",
              amount: "${finalAmount.toFixed(2)}"
            }
          ]`;
      } else {
        // For Membership payments
        let membershipAmount = selectedMembership?.price || 0;

        // Apply selected discount
        let discountAmount = 0;
        if (selectedDiscount) {
          if (selectedDiscount.type === 'percentage') {
            discountAmount = membershipAmount * (selectedDiscount.discountValue / 100);
            membershipAmount = membershipAmount * (1 - selectedDiscount.discountValue / 100);
          } else if (selectedDiscount.type === 'fixed') {
            discountAmount = Math.min(membershipAmount, selectedDiscount.discountValue);
            membershipAmount = Math.max(0, membershipAmount - selectedDiscount.discountValue);
          }
        }

        let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
        let subtotal = membershipAmount + bundleCost;

        // Check if VAT is enabled for this company
        const isTaxEnabled = companySettings?.isTaxEnabled !== false;
        const vatLineItem = isTaxEnabled ? `,
            {
              label: "VAT (15%)",
              amount: "${(subtotal * 0.15).toFixed(2)}"
            }` : '';

        const baseAmount = selectedMembership?.price || 0;
        const discountLineItem = selectedDiscount ? `,
            {
              label: "${selectedDiscount.label}${selectedDiscount.type === 'percentage' ? ` (${selectedDiscount.discountValue}%)` : ''}",
              amount: "-${discountAmount.toFixed(2)}"
            }` : '';

        lineItemsConfig = `[
            {
              label: "Membership Fee",
              amount: "${baseAmount.toFixed(2)}"
            }${discountLineItem}${paymentDetails.addClothes && paymentDetails.bundleAmount ? `,
            {
              label: "Bundle",
              amount: "${bundleCost.toFixed(2)}"
            }` : ''}${vatLineItem}
          ]`;
      }

      // Clear and create form container
      const container = document.querySelector('.payment-widget-wrapper');
      if (container) {
        container.innerHTML = '';

        const form = document.createElement('form');
        form.className = 'paymentWidgets';
        form.action = `${window.location.origin}/payment-callback.html`;
        form.setAttribute('data-brands', 'APPLEPAY');

        container.appendChild(form);
      }

      // Create HyperPay options with Apple Pay configuration
      const optionsScript = document.createElement('script');
      optionsScript.setAttribute('data-wpwl-options', 'true');
      const applePayFormIntegrity = sessionStorage.getItem('hyperPayIntegrity') || '';
      // merchantIdentifier: "8ac7a4c79483092601948366b9d1011b", --test
      optionsScript.innerHTML = `
      var wpwlOptions = {
        ${applePayFormIntegrity ? `integrity: "${applePayFormIntegrity}",` : ''}
        paymentTarget: "_top",
        style: "plain",
        locale: "en",
        applePay: {
          version: 3,
          checkAvailability: "applePayCapabilities",
          merchantIdentifier: "8acda4cd9836ada901987a5594615b2d",
          displayName: "Al Hilal Academy",
          total: { 
            label: "AL HILAL ACADEMY",
            amount: "${finalAmount.toFixed(2)}",
            type: "final"
          },
          currencyCode: "${currencyCode}",
          countryCode: "${currencyCode === 'KWD' ? 'KW' : 'SA'}",
          supportedNetworks: ["mada", "masterCard", "visa", "amex"],
          merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
          buttonStyle: "black",
          buttonType: "pay",
          buttonSource: "js",
          lineItems: ${lineItemsConfig},
          onPaymentAuthorized: function(payment) {
            console.log('Apple Pay payment authorized:', payment);
            // Return success to complete the Apple Pay flow
            return {
              status: "SUCCESS"
            };
          },
          onCancel: function() {
            console.log('Apple Pay cancelled by user');
          },
          onPaymentMethodSelected: function(paymentMethod) {
            console.log('Apple Pay method selected:', paymentMethod);
            return {
              newTotal: {
                label: "AL HILAL ACADEMY",
                amount: "${finalAmount.toFixed(2)}",
                type: "final"
              }
            };
          }
        },
        onReady: function() {
          console.log('HyperPay Apple Pay form ready');
          window.setWidgetLoading && window.setWidgetLoading(false);
        },
        onError: function(error) {
          console.error('HyperPay Apple Pay error:', error);
          window.setWidgetLoading && window.setWidgetLoading(false);
          alert('Apple Pay error: ' + (error.message || 'Please try again'));
        }
      };
    `;
      document.head.appendChild(optionsScript);

      // Store the setWidgetLoading function globally
      window.setWidgetLoading = setWidgetLoading;

      // Load HyperPay script
      const script = document.createElement('script');
      // for test
      // script.src = `https://eu-test.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
      script.src = `https://eu-prod.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
      script.async = true;

      script.onload = () => {
        console.log('HyperPay Apple Pay script loaded successfully');
      };

      script.onerror = () => {
        console.error('Failed to load HyperPay Apple Pay script');
        setError('Failed to load Apple Pay form. Please check your internet connection and try again.');
        setWidgetLoading(false);
        // Return to payment method selection if there's an error
        setShowPaymentForm(false);
        setShowPaymentMethodSelection(true);
        setSelectedPaymentMethod(null);
      };

      document.head.appendChild(script);

    } catch (error) {
      console.error('Apple Pay error:', error);
      setError('Apple Pay is not available. Please select a different payment method.');
      setWidgetLoading(false);
      setShowPaymentForm(false);
      setShowPaymentMethodSelection(true);
      setSelectedPaymentMethod(null);
    }
  };

  // Transfer: the stored amount to pay (after discount, VAT and points), from the payment log.
  // Falls back to the old list-price computation only if the log carries no amount.
  const getTransferAmountToPay = () => {
    const stored = parseFloat(paymentDetails?.amount);
    if (!isNaN(stored) && stored > 0) {
      return Math.round(stored * 100) / 100;
    }
    const membershipPrice = selectedMembership?.price || 0;
    return membershipPrice * 1.15 - (paymentDetails?.pointsUsed || 0);
  };

  // Transfer: the total before points (after discount, incl. VAT) = amount to pay + points used.
  const getTransferGrossTotal = () => getTransferAmountToPay() + (paymentDetails?.pointsUsed || 0);

  // Calculate total amount
  const calculateTotalAmount = () => {
    // For Product payments, use productPrice and productVat directly
    if (paymentType === 'Product') {
      return (productPrice || 0) + (productVat || 0);
    }

    // For Transfer payments, the amount to pay is the one stored on the payment log: checkout charges
    // exactly that (discount, VAT and points already applied when the transfer was approved).
    // Recomputing it here as list price x 1.15 - points ignored the discount, so the page showed and
    // sent a different amount from the one charged (HyperPay 100.550.701 "amounts not matched").
    if (paymentType === 'Transfer') {
      return getTransferAmountToPay();
    }

    // For Membership payments
    let membershipAmount = selectedMembership?.price || 0;

    // Apply selected online discount only (visible discount)
    if (selectedDiscount) {
      if (selectedDiscount.type === 'percentage') {
        membershipAmount = membershipAmount * (1 - selectedDiscount.discountValue / 100);
      } else if (selectedDiscount.type === 'fixed') {
        membershipAmount = Math.max(0, membershipAmount - selectedDiscount.discountValue);
      }
    }
    // Apply hidden discount if no visible discount is selected
    else if (hiddenDiscount) {
      if (hiddenDiscount.type === 'percentage') {
        membershipAmount = membershipAmount * (1 - hiddenDiscount.discountValue / 100);
      } else if (hiddenDiscount.type === 'fixed') {
        membershipAmount = Math.max(0, membershipAmount - hiddenDiscount.discountValue);
      }
    }

    let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
    let subtotal = membershipAmount + bundleCost;

    // Apply VAT only if company has tax enabled (default to true if not set)
    const isTaxEnabled = companySettings?.isTaxEnabled !== false;
    let totalWithVat = isTaxEnabled ? subtotal * 1.15 : subtotal;

    // Deduct wallet points if player chose to apply them
    if (applyWalletPoints) {
      const walletPoints = player?.walletPoints?.balancePoints || 0;
      const pointsToApply = Math.min(walletPoints, totalWithVat);
      return Math.max(0, totalWithVat - pointsToApply);
    }

    return totalWithVat;
  };

  // Create card payment form - handles specific card types
  const createCardPaymentForm = (cardType = 'card') => {
    console.log('Creating card payment form for:', cardType);

    // Clean up any existing scripts first
    cleanupHyperPayScripts();

    // Calculate total amount
    const cardPaymentAmount = calculateTotalAmount();

    // Map card types to HyperPay brands
    let dataBrands;
    const isHttps = window.location.protocol === 'https:';

    switch (cardType) {
      case 'visa':
        dataBrands = isHttps ? 'VISA APPLEPAY' : 'VISA';
        break;
      case 'mastercard':
        dataBrands = isHttps ? 'MASTER APPLEPAY' : 'MASTER';
        break;
      case 'mada':
        dataBrands = isHttps ? 'MADA APPLEPAY' : 'MADA';
        break;
      case 'tamara':
        dataBrands = 'TAMARA';
        break;
      default:
        dataBrands = isHttps ? 'VISA MASTER MADA APPLEPAY' : 'VISA MASTER MADA';
    }

    console.log('Using data-brands:', dataBrands);

    // Clear and create form container
    const container = document.querySelector('.payment-widget-wrapper');
    if (container) {
      container.innerHTML = '';

      const form = document.createElement('form');
      form.className = 'paymentWidgets';
      form.action = `${window.location.origin}/payment-callback.html`;
      form.setAttribute('data-brands', dataBrands);

      container.appendChild(form);
    }

    // Create HyperPay options
    const optionsScript = document.createElement('script');
    optionsScript.setAttribute('data-wpwl-options', 'true');

    // Read integrity hash stored during checkout creation
    const hyperPayIntegrity = sessionStorage.getItem('hyperPayIntegrity') || '';

    // Calculate amounts for Apple Pay
    const totalAmount = calculateTotalAmount();

    // Build line items based on payment type
    let lineItemsConfig = '';

    if (paymentType === 'Product') {
      // For Product payments, show product price and VAT
      const productVatLineItem = productVat > 0 ? `,
            {
              label: "VAT (${(productVatPercentage || 0).toFixed(1)}%)",
              amount: "${(productVat || 0).toFixed(2)}"
            }` : '';

      lineItemsConfig = `[
            {
              label: "Product Price",
              amount: "${(productPrice || 0).toFixed(2)}"
            }${productVatLineItem}
          ]`;
    } else if (paymentType === 'Transfer') {
      // One line: the stored amount (discount, VAT and points already applied), equal to the total.
      lineItemsConfig = `[
            {
              label: "Branch Transfer",
              amount: "${totalAmount.toFixed(2)}"
            }
          ]`;
    } else {
      // For Membership payments
      let membershipAmount = selectedMembership?.price || 0;

      // Apply selected discount or hidden discount
      let discountAmount = 0;
      const activeDiscount = selectedDiscount || hiddenDiscount;

      if (activeDiscount) {
        if (activeDiscount.type === 'percentage') {
          discountAmount = membershipAmount * (activeDiscount.discountValue / 100);
          membershipAmount = membershipAmount * (1 - activeDiscount.discountValue / 100);
        } else if (activeDiscount.type === 'fixed') {
          discountAmount = Math.min(membershipAmount, activeDiscount.discountValue);
          membershipAmount = Math.max(0, membershipAmount - activeDiscount.discountValue);
        }
      }

      let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
      let subtotal = membershipAmount + bundleCost;

      // Check if VAT is enabled for this company
      const isTaxEnabled = companySettings?.isTaxEnabled !== false;
      const vatLineItem = isTaxEnabled ? `,
            {
              label: "VAT (15%)",
              amount: "${(subtotal * 0.15).toFixed(2)}"
            }` : '';

      const baseAmount = selectedMembership?.price || 0;
      const discountLineItem = selectedDiscount ? `,
            {
              label: "${selectedDiscount.label}${selectedDiscount.type === 'percentage' ? ` (${selectedDiscount.discountValue}%)` : ''}",
              amount: "-${discountAmount.toFixed(2)}"
            }` : '';

      lineItemsConfig = `[
            {
              label: "Membership Fee",
              amount: "${baseAmount.toFixed(2)}"
            }${discountLineItem}${paymentDetails.addClothes && paymentDetails.bundleAmount ? `,
            {
              label: "Bundle",
              amount: "${bundleCost.toFixed(2)}"
            }` : ''}${vatLineItem}
          ]`;
    }

    // Include Apple Pay configuration if HTTPS
    // merchantIdentifier: "8ac7a4c79483092601948366b9d1011b", --test
    const applePayConfig = isHttps ? `
        applePay: {
          version: 3,
          checkAvailability: "applePayCapabilities",
          merchantIdentifier: "8acda4cd9836ada901987a5594615b2d",
          displayName: "Al Hilal Academy",
          total: { 
            label: "AL HILAL ACADEMY",
            amount: "${totalAmount.toFixed(2)}",
            type: "final"
          },
          currencyCode: "${currencyCode}",
          countryCode: "${currencyCode === 'KWD' ? 'KW' : 'SA'}",
          supportedNetworks: ["mada", "masterCard", "visa", "amex"],
          merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"],
          buttonStyle: "black",
          buttonType: "pay",
          buttonSource: "js",
          lineItems: ${lineItemsConfig},
          onPaymentAuthorized: function(payment) {
            console.log('Apple Pay payment authorized:', payment);
            return {
              status: "SUCCESS"
            };
          },
          onCancel: function() {
            console.log('Apple Pay cancelled by user');
          },
          onPaymentMethodSelected: function(paymentMethod) {
            console.log('Apple Pay method selected:', paymentMethod);
            return {
              newTotal: {
                label: "AL HILAL ACADEMY",
                amount: "${totalAmount.toFixed(2)}",
                type: "final"
              }
            };
          }
        },` : '';

    optionsScript.innerHTML = `
      var wpwlOptions = {${applePayConfig}
        ${hyperPayIntegrity ? `integrity: "${hyperPayIntegrity}",` : ''}
        paymentTarget: "_top",
        style: "plain",
        locale: "en",
        brandDetection: false,
        showCVVHint: true,
        autofocus: "card.number",
        requireCvv: true,
        allowEmptyCardHolderName: false,
        showBrand: false,
        onReady: function() {
          console.log('HyperPay form ready for card payment');
          // Hide brand selection since user already selected payment method
          setTimeout(() => {
            const brandGroup = document.querySelector('.wpwl-group-brand');
            if (brandGroup) {
              brandGroup.style.display = 'none';
            }
            window.setWidgetLoading && window.setWidgetLoading(false);
          }, 200);
        },
        onError: function(error) {
          console.error('HyperPay error:', error);
          window.setWidgetLoading && window.setWidgetLoading(false);
          alert('Payment error: ' + (error.message || 'Please try again'));
        },
        labels: {
          cardHolder: "Cardholder Name",
          cardNumber: "Card Number", 
          cvv: "CVV",
          expiryDate: "MM/YY",
          submit: "Complete Payment"
        }
      };
    `;
    document.head.appendChild(optionsScript);

    // Add custom CSS for card form
    const customCSS = document.createElement('style');
    customCSS.setAttribute('data-hyperpay-custom', 'true');
    customCSS.innerHTML = `
      .wpwl-container {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
      }

      .wpwl-form {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      .wpwl-group {
        margin-bottom: 1.5rem !important;
        position: relative !important;
      }

      .wpwl-group-brand {
        display: none !important;
      }

      .wpwl-label {
        font-weight: 600 !important;
        color: #374151 !important;
        font-size: 14px !important;
        margin-bottom: 0.5rem !important;
        display: block !important;
      }

      .wpwl-control {
        width: 100% !important;
        padding: 12px 16px !important;
        border: 2px solid #e5e7eb !important;
        border-radius: 12px !important;
        font-size: 16px !important;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        background-color: #ffffff !important;
        color: #111827 !important;
        transition: all 0.2s ease !important;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
        outline: none !important;
        min-height: 48px !important;
        box-sizing: border-box !important;
      }

      .wpwl-wrapper-cardNumber,
      .wpwl-wrapper-cvv,
      .wpwl-wrapper-expiry,
      .wpwl-wrapper-cardHolder {
        border: 2px solid #e5e7eb !important;
        border-radius: 12px !important;
        background-color: #ffffff !important;
        transition: all 0.2s ease !important;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
      }

      .wpwl-wrapper-cardNumber:focus-within,
      .wpwl-wrapper-cvv:focus-within,
      .wpwl-wrapper-expiry:focus-within,
      .wpwl-wrapper-cardHolder:focus-within {
        border-color: #040c4b !important;
        box-shadow: 0 0 0 3px rgba(4, 12, 75, 0.1) !important;
      }

      .wpwl-wrapper iframe {
        height: 48px !important;
        border: none !important;
        border-radius: 12px !important;
        background: transparent !important;
        width: 100% !important;
      }

      .wpwl-button {
        width: 100% !important;
        background: linear-gradient(135deg, #040c4b 0%, #1e3a8a 100%) !important;
        color: white !important;
        border: none !important;
        border-radius: 12px !important;
        padding: 16px 24px !important;
        font-size: 16px !important;
        font-weight: 600 !important;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
        cursor: pointer !important;
        transition: all 0.2s ease !important;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
      }

      .wpwl-button:hover {
        background: linear-gradient(135deg, #1e3a8a 0%, #040c4b 100%) !important;
        transform: translateY(-1px) !important;
      }
    `;
    document.head.appendChild(customCSS);

    // Store the setWidgetLoading function globally
    window.setWidgetLoading = setWidgetLoading;

    // Load HyperPay script with better error handling
    const script = document.createElement('script');
    // for test
    // script.src = `https://eu-test.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.src = `https://eu-prod.oppwa.com/v1/paymentWidgets.js?checkoutId=${checkoutId}`;
    script.async = true;

    script.onload = () => {
      console.log('HyperPay script loaded successfully');
      // The onReady callback will handle hiding the loading state
    };

    script.onerror = () => {
      console.error('Failed to load HyperPay script');
      setError('Failed to load payment form. Please check your internet connection and try again.');
      setWidgetLoading(false);
    };

    // Set a timeout fallback in case the script takes too long
    const timeout = setTimeout(() => {
      if (widgetLoading) {
        console.warn('HyperPay script loading timeout');
        setError('Payment form is taking longer than expected. Please try again.');
        setWidgetLoading(false);
      }
    }, 10000); // 10 second timeout

    // Clear timeout when script loads
    script.onload = () => {
      console.log('HyperPay script loaded successfully');
      clearTimeout(timeout);
    };

    document.head.appendChild(script);
  };

  const createTamaraPaymentForm = async () => {
    console.log('Creating Tamara payment form');
    try {
      cleanupHyperPayScripts();

      const payload = {
        player_id: parseInt(paymentDetails.playerId),
        membership: parseInt(selectedMembership?.value || paymentDetails.membershipId),
        team: parseInt(paymentDetails.team || player?.currentMembership?.teamId || 983),
        session: parseInt(paymentDetails.session || player?.currentMembership?.sessionId || 519),
        age_group: parseInt(paymentDetails.ageGroup || player?.currentMembership?.ageGroupId || 165),
        branch: parseInt(paymentDetails.branch || player?.currentMembership?.branchId || 57),
        start_date: paymentDetails.startDate || "2025-07-03",
        comment: paymentDetails.comment || "",
        inv_comment: paymentDetails.invComment || "",
        inv_payment_status: "pending",
        inv_payment_method: "HyperPay",
        inv_discount: parseFloat(paymentDetails.invDiscount) || 0,
        add_clothes: paymentDetails.addClothes || false,
        discount_percent: parseInt(paymentDetails.discountPercent) || 0,
        online_discount_id: selectedDiscount ? parseInt(selectedDiscount.value) : (hiddenDiscount ? parseInt(hiddenDiscount.value) : null),
        payment_log_guid: paymentDetails.paymentLogGuid,
        payment_code: paymentReferenceNo,
        currency: currencyCode,
        player_name: player?.fullName || player?.localizedFullName || "N/A",
        payment_type: paymentType,
        payment_brand: 'TAMARA',
      };

      const response = await axios.post(
        `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/hyperpay/checkout`,
        payload,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { id: tamaraCheckoutId, integrity: tamaraIntegrity } = response.data;

      if (!tamaraCheckoutId) {
        setError("Failed to initialize Tamara payment. Please try again.");
        setWidgetLoading(false);
        return;
      }

      sessionStorage.setItem('hyperPayCheckoutId', tamaraCheckoutId);
      sessionStorage.setItem('hyperPayIntegrity', tamaraIntegrity || '');

      // Create form container with TAMARA brand
      const container = document.querySelector('.payment-widget-wrapper');
      if (container) {
        container.innerHTML = '';
        const form = document.createElement('form');
        form.className = 'paymentWidgets';
        form.action = `${window.location.origin}/payment-callback.html`;
        form.setAttribute('data-brands', 'TAMARA');
        container.appendChild(form);
      }

      // Load HyperPay script with Tamara checkout ID
      const script = document.createElement('script');
      // script.src = `https://eu-test.oppwa.com/v1/paymentWidgets.js?checkoutId=${tamaraCheckoutId}`;
      script.src = `https://eu-prod.oppwa.com/v1/paymentWidgets.js?checkoutId=${tamaraCheckoutId}`;
      script.async = true;

      script.onload = () => {
        console.log('Tamara HyperPay script loaded successfully');
        setWidgetLoading(false);
      };

      script.onerror = () => {
        console.error('Failed to load Tamara HyperPay script');
        setError('Failed to load Tamara payment form. Please try again.');
        setWidgetLoading(false);
      };

      document.head.appendChild(script);
    } catch (err) {
      console.error('Tamara payment initialization error:', err);
      setError('Tamara payment initialization failed. Please try again.');
      setWidgetLoading(false);
    }
  };

  const createTabbyPaymentForm = async () => {
    console.log('Creating Tabby payment form');
    try {
      cleanupHyperPayScripts();

      const payload = {
        player_id: parseInt(paymentDetails.playerId),
        membership: parseInt(selectedMembership?.value || paymentDetails.membershipId),
        team: parseInt(paymentDetails.team || player?.currentMembership?.teamId || 983),
        session: parseInt(paymentDetails.session || player?.currentMembership?.sessionId || 519),
        age_group: parseInt(paymentDetails.ageGroup || player?.currentMembership?.ageGroupId || 165),
        branch: parseInt(paymentDetails.branch || player?.currentMembership?.branchId || 57),
        start_date: paymentDetails.startDate || "2025-07-03",
        comment: paymentDetails.comment || "",
        inv_comment: paymentDetails.invComment || "",
        inv_payment_status: "pending",
        inv_payment_method: "HyperPay",
        inv_discount: parseFloat(paymentDetails.invDiscount) || 0,
        add_clothes: paymentDetails.addClothes || false,
        discount_percent: parseInt(paymentDetails.discountPercent) || 0,
        online_discount_id: selectedDiscount ? parseInt(selectedDiscount.value) : (hiddenDiscount ? parseInt(hiddenDiscount.value) : null),
        payment_log_guid: paymentDetails.paymentLogGuid,
        payment_code: paymentReferenceNo,
        currency: currencyCode,
        player_name: player?.fullName || player?.localizedFullName || "N/A",
        payment_type: paymentType,
        payment_brand: 'TABBY',
      };

      const response = await axios.post(
        `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/hyperpay/checkout`,
        payload,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { id: tabbyCheckoutId, integrity: tabbyIntegrity } = response.data;

      if (!tabbyCheckoutId) {
        setError("Failed to initialize Tabby payment. Please try again.");
        setWidgetLoading(false);
        return;
      }

      sessionStorage.setItem('hyperPayCheckoutId', tabbyCheckoutId);
      sessionStorage.setItem('hyperPayIntegrity', tabbyIntegrity || '');

      const container = document.querySelector('.payment-widget-wrapper');
      if (container) {
        container.innerHTML = '';
        const form = document.createElement('form');
        form.className = 'paymentWidgets';
        form.action = `${window.location.origin}/payment-callback.html`;
        form.setAttribute('data-brands', 'TABBY');
        container.appendChild(form);
      }

      const script = document.createElement('script');
      script.src = `https://eu-prod.oppwa.com/v1/paymentWidgets.js?checkoutId=${tabbyCheckoutId}`;
      script.async = true;
      script.onload = () => { console.log('Tabby HyperPay script loaded successfully'); setWidgetLoading(false); };
      script.onerror = () => { setError('Failed to load Tabby payment form.'); setWidgetLoading(false); };
      document.head.appendChild(script);
    } catch (err) {
      console.error('Tabby payment initialization error:', err);
      setError('Tabby payment initialization failed. Please try again.');
      setWidgetLoading(false);
    }
  };

  // Apply promo code
  const handleApplyPromoCode = async () => {
    const trimmedCode = promoCode.trim();
    if (!trimmedCode) {
      setPromoError('Please enter a promo code.');
      return;
    }

    try {
      setIsApplyingPromo(true);
      setPromoError('');

      const response = await axios.get(
        `${appConfig.DotNet_Api_Endpoint}/discount/promo/${encodeURIComponent(trimmedCode)}`
      );

      const discount = response.data;

      if (discount && discount.discountId) {
        // Map to the same shape as discountOptions items
        const promoDiscount = {
          value: discount.discountId.toString(),
          label: discount.name,
          description: discount.description || '',
          type: discount.type,
          discountValue: discount.value,
          code: discount.code,
          isPromo: true,
        };

        setSelectedDiscount(promoDiscount);
        setHiddenDiscount(null);
        setPromoApplied(true);
        setPromoError('');
      } else {
        setPromoError('Invalid or expired promo code.');
      }
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setPromoError('Invalid or expired promo code.');
      } else {
        setPromoError('Failed to apply promo code. Please try again.');
      }
    } finally {
      setIsApplyingPromo(false);
    }
  };

  // Remove applied promo code
  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(false);
    setPromoError('');
    if (selectedDiscount?.isPromo) {
      setSelectedDiscount(null);
    }
  };

  const handlePayNow = async () => {
    try {
      setLoading(true);

      // Calculate total amount consistently with backend
      const finalAmount = calculateTotalAmount();

      console.log('Payment calculation:', {
        finalAmount,
        paymentProvider
      });

      // Check if MyFatoorah provider
      if (paymentProvider === 'MyFatoorah') {
        // Handle MyFatoorah payment flow
        await handleMyFatoorahPayment(finalAmount);
        return;
      }

      // Handle HyperPay payment flow (existing code)
      const payload = {
        player_id: parseInt(paymentDetails.playerId),
        membership: parseInt(selectedMembership?.value || paymentDetails.membershipId),
        team: parseInt(paymentDetails.team || player?.currentMembership?.teamId || 983),
        session: parseInt(paymentDetails.session || player?.currentMembership?.sessionId || 519),
        age_group: parseInt(paymentDetails.ageGroup || player?.currentMembership?.ageGroupId || 165),
        branch: parseInt(paymentDetails.branch || player?.currentMembership?.branchId || 57),
        start_date: paymentDetails.startDate || "2025-07-03",
        comment: paymentDetails.comment || "",
        inv_comment: paymentDetails.invComment || "",
        inv_payment_status: "pending",
        inv_payment_method: "HyperPay",
        inv_discount: parseFloat(paymentDetails.invDiscount) || 0,
        add_clothes: paymentDetails.addClothes || false,
        discount_percent: parseInt(paymentDetails.discountPercent) || 0,
        online_discount_id: selectedDiscount ? parseInt(selectedDiscount.value) : (hiddenDiscount ? parseInt(hiddenDiscount.value) : null),
        payment_log_guid: paymentDetails.paymentLogGuid,
        payment_code: paymentReferenceNo,
        currency: currencyCode,
        player_name: player?.fullName || player?.localizedFullName || "N/A",
        payment_type: paymentType, // Add payment type so backend knows to use transaction log amount for Transfer
        points_used: (() => {
          if (!applyWalletPoints) return 0;
          const walletPts = player?.walletPoints?.balancePoints || 0;
          if (walletPts <= 0) return 0;
          // Gross total before points deduction
          let amt = selectedMembership?.price || 0;
          const activeDiscount = selectedDiscount || hiddenDiscount;
          if (activeDiscount) {
            if (activeDiscount.type === 'percentage') amt = amt * (1 - activeDiscount.discountValue / 100);
            else if (activeDiscount.type === 'fixed') amt = Math.max(0, amt - activeDiscount.discountValue);
          }
          const bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
          const subtotal = amt + bundleCost;
          const isTaxEnabled = companySettings?.isTaxEnabled !== false;
          const grossTotal = isTaxEnabled ? subtotal * 1.15 : subtotal;
          return Math.min(walletPts, grossTotal);
        })(),
      };

      console.log('Sending payment payload:', payload);

      // Call your backend API to generate HyperPay payment URL
      const response = await axios.post(
        `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/hyperpay/checkout`,
        payload,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Extract values according to HyperPay documentation response format
      const {
        id: checkoutId,
        integrity,
        result,
        buildNumber,
        timestamp,
        ndc
      } = response.data;

      console.log('HyperPay Response:', response.data);
      console.log('Extracted checkoutId:', checkoutId);
      console.log('Result:', result);

      // Check if the result indicates success (codes starting with 000.200 are successful)
      if (result?.code && !result.code.startsWith("000.200")) {
        console.warn('HyperPay checkout creation warning:', result);
        setError(`Payment setup warning: ${result.description || 'Unknown error'}`);
        return;
      }

      if (checkoutId) {
        // Store checkout details for later verification
        sessionStorage.setItem('hyperPayCheckoutId', checkoutId);
        sessionStorage.setItem('paymentReferenceNo', paymentReferenceNo);
        sessionStorage.setItem('expectedAmount', finalAmount.toFixed(2));
        sessionStorage.setItem('hyperPayIntegrity', integrity || '');

        // Store checkoutId and show payment method selection
        setCheckoutId(checkoutId);
        setShowPaymentMethodSelection(true);

      } else {
        console.error('No checkoutId received in response:', response.data);
        setError("Failed to initialize payment. Please try again.");
      }
    } catch (err) {
      console.error("Payment error details:", err.response?.data || err.message);
      setError("Payment initialization failed. Please try again.");
      console.error("Payment error:", err);
    } finally {
      setLoading(false);
    }
  };

  // MyFatoorah payment handler
  const handleMyFatoorahPayment = async (amount) => {
    try {
      console.log('Initiating MyFatoorah payment for amount:', amount);

      // Get company ID from company settings
      const branchId = paymentDetails.branch || player?.currentMembership?.branchId || 57;
      const branchCompanyResponse = await axios.get(
        `${appConfig.DotNet_Api_Endpoint}/Branch/public/${branchId}/company`
      );
      const companyId = branchCompanyResponse.data.companyId;

      // Step 1: Initiate Payment to get payment methods
      const initiateResponse = await axios.post(
        `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/myfatoorah/initiate?companyId=${companyId}`,
        {
          // ReferenceId identifies the payment log the server prices this payment from.
          // The server ignores Amount and charges the logged amount (hotfix H7).
          ReferenceId: paymentReferenceNo,
          Amount: amount,
          Currency: currencyCode
        }
      );

      console.log('MyFatoorah Initiate Response:', initiateResponse.data);

      if (!initiateResponse.data.isSuccess) {
        setError('Failed to initialize payment. Please try again.');
        return;
      }

      // Store payment methods and show selection UI
      // The response structure is: data.data.Data.PaymentMethods
      const paymentMethods = initiateResponse.data.data?.Data?.PaymentMethods || [];
      console.log('Payment Methods extracted:', paymentMethods);
      setMyFatoorahPaymentMethods(paymentMethods);
      setShowPaymentMethodSelection(true);

    } catch (err) {
      console.error('MyFatoorah payment error:', err);
      setError('Payment initialization failed. Please try again.');
    }
  };

  // Execute MyFatoorah payment with selected method
  const executeMyFatoorahPayment = async () => {
    if (!selectedMyFatoorahMethod) {
      setError('Please select a payment method');
      return;
    }

    try {
      setLoading(true);

      // Get company ID
      const branchId = paymentDetails.branch || player?.currentMembership?.branchId || 57;
      const branchCompanyResponse = await axios.get(
        `${appConfig.DotNet_Api_Endpoint}/Branch/public/${branchId}/company`
      );
      const companyId = branchCompanyResponse.data.companyId;

      // Calculate final amount
      const finalAmount = calculateTotalAmount();

      // Step 2: Execute Payment with selected payment method
      const executeResponse = await axios.post(
        `${appConfig.DotNet_Api_Endpoint}/OnlinePayment/myfatoorah/execute?companyId=${companyId}`,
        {
          PaymentMethodId: selectedMyFatoorahMethod.PaymentMethodId,
          CustomerName: player?.fullName || player?.localizedFullName || '',
          PaymentId: paymentReferenceNo,
          CallbackUrl: `${window.location.origin}/#/payment-result?ref=${paymentReferenceNo}`,
          Amount: finalAmount,
          CustomerEmail: player?.email || '',
          CustomerMobile: player?.phoneNumber || '',
          MobileCountryCode: '+965'
        }
      );

      console.log('MyFatoorah Execute Response:', executeResponse.data);

      if (executeResponse.data.isSuccess && executeResponse.data.paymentUrl) {
        // Redirect to MyFatoorah payment page
        window.location.href = executeResponse.data.paymentUrl;
      } else {
        setError('Failed to generate payment URL. Please try again.');
        setLoading(false);
      }

    } catch (err) {
      console.error('MyFatoorah execute payment error:', err);
      setError('Payment execution failed. Please try again.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Clear any existing payment status interval
    const intervalId = sessionStorage.getItem('paymentStatusInterval');
    if (intervalId) {
      clearInterval(parseInt(intervalId));
      sessionStorage.removeItem('paymentStatusInterval');
    }

    // Remove HyperPay script and clean up
    const existingScript = document.querySelector('script[src*="paymentWidgets.js"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Remove wpwlOptions script
    const existingOptionsScript = document.querySelector('script[data-wpwl-options]');
    if (existingOptionsScript) {
      existingOptionsScript.remove();
    }

    // Remove custom CSS
    const customCSS = document.querySelector('style[data-hyperpay-custom]');
    if (customCSS) {
      customCSS.remove();
    }

    // Reset payment form state
    setShowPaymentForm(false);
    setCheckoutId(null);
    setWidgetLoading(false);

    window.location.href = "/";
  };

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      // Format the completed payment data for receipt generation
      const formattedData = formatPaymentDataForReceipt({
        id: paymentDetails?.referenceNumber || paymentDetails?.transactionId || 'N/A',
        amount: paymentDetails?.amount || '0.00',
        currency: paymentDetails?.currency || 'SAR',
        paymentBrand: paymentDetails?.paymentMethod || 'Card',
        description: 'Al Hilal Academy Membership Payment',
        timestamp: paymentDetails?.transactionDate || new Date().toISOString(),
        productName: 'Academy Membership'
      });

      // Create player details object for receipt
      const playerDetailsForReceipt = {
        fullName: player?.fullName || player?.localizedFullName || paymentDetails?.playerName || 'N/A',
        playerId: player?.playerId || paymentDetails?.playerId || 'N/A',
        phoneNumber: player?.phoneNumber || paymentDetails?.phoneNumber || 'N/A',
        membershipName: paymentDetails?.membershipName || 'N/A',
        branchName: paymentDetails?.branchName || 'N/A',
        startDate: paymentDetails?.startDate || 'N/A',
        expiryDate: paymentDetails?.expiryDate || 'N/A',
        paymentType: paymentType || paymentDetails?.paymentType || null,
        playerName: player?.fullName || player?.localizedFullName || paymentDetails?.playerName || 'N/A',
        pointsUsed: paymentDetails?.pointsUsed || 0 // Add pointsUsed for Transfer type
      };

      const success = downloadReceipt(formattedData, playerDetailsForReceipt);
      if (success) {
        // Show success message for a brief moment
        setTimeout(() => {
          setDownloadingReceipt(false);
        }, 1000);
      } else {
        setDownloadingReceipt(false);
        alert('Error generating receipt. Please try again.');
      }
    } catch (error) {
      setDownloadingReceipt(false);
      console.error('Receipt download error:', error);
      alert('Error generating receipt. Please try again.');
    }
  };

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      const intervalId = sessionStorage.getItem('paymentStatusInterval');
      if (intervalId) {
        clearInterval(parseInt(intervalId));
        sessionStorage.removeItem('paymentStatusInterval');
      }

      // Remove HyperPay script on unmount
      const existingScript = document.querySelector('script[src*="paymentWidgets.js"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Remove wpwlOptions script on unmount
      const existingOptionsScript = document.querySelector('script[data-wpwl-options]');
      if (existingOptionsScript) {
        existingOptionsScript.remove();
      }

      // Remove custom CSS on unmount
      const customCSS = document.querySelector('style[data-hyperpay-custom]');
      if (customCSS) {
        customCSS.remove();
      }

      // Remove discount card CSS on unmount
      const discountCardCSS = document.querySelector('style[data-discount-card-css]');
      if (discountCardCSS) {
        discountCardCSS.remove();
      }
    };
  }, []);

  if (loading && !isPaymentCompleted) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="min-vh-100 bg-light d-flex flex-column">
        {/* Header */}
        <div
          className="text-white py-0 px-4 d-flex align-items-center"
          style={{ backgroundColor: "#040c4b" }}
        >
          <div className="logo-wrapper">
            <img
              className=""
              src="/AcademyLogoBlue.png"
              style={{
                filter: "invert(1) grayscale(1) brightness(2)",
                width: "55px",
                height: "auto",
              }}
              alt="Al Hilal Academy Logo"
            />
          </div>
          <h5 className="mb-0 fw-bold text-white">Al Hilal Academy</h5>
        </div>

        {/* Error Content */}
        <div className="flex-grow-1 d-flex justify-content-center align-items-center pt-5 pb-4">
          <div
            className="bg-white shadow p-4"
            style={{ maxWidth: 500, width: "100%", borderRadius: "1rem" }}
          >
            <div className="text-center">
              <div className="mb-3">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dc3545"
                  strokeWidth="2"
                  className="mx-auto"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <h4 className="fw-bold mb-3 text-danger">
                {isPaymentExpired ? 'Payment Expired' : 'Payment Unavailable'}
              </h4>
              <p className="text-muted mb-4">{error}</p>

              {isPaymentExpired ? (
                <div className="d-flex flex-column gap-2">
                  <button
                    className="btn"
                    style={{
                      backgroundColor: resendingSms ? "#6c757d" : "#040c4b",
                      color: "white",
                      opacity: resendingSms ? 0.6 : 1,
                      cursor: resendingSms ? "not-allowed" : "pointer"
                    }}
                    onClick={handleResendSms}
                    disabled={resendingSms}
                  >
                    {resendingSms ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resending SMS...
                      </>
                    ) : (
                      'Resend Payment SMS'
                    )}
                  </button>

                  {/* Resend message display */}
                  {resendMessage && (
                    <div
                      className={`alert ${resendMessage.includes('Failed') ? 'alert-danger' : 'alert-success'} mt-2`}
                      role="alert"
                    >
                      <i className={`fas ${resendMessage.includes('Failed') ? 'fa-exclamation-circle' : 'fa-check-circle'} me-2`}></i>
                      {resendMessage}
                    </div>
                  )}

                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => window.history.back()}
                  >
                    Go Back
                  </button>
                </div>
              ) : (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => window.history.back()}
                >
                  Go Back
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug log before render
  console.log('PaymentPreview render state:', {
    isPaymentCompleted,
    loading,
    error,
    hasPaymentDetails: !!paymentDetails,
    paymentReferenceNo
  });

  return (
    <>
      <style>
        {`
          .disabled-select-white .ant-select-selector {
            background-color: #ffffff !important;
            border-color: #d9d9d9 !important;
            cursor: not-allowed !important;
          }
          .disabled-select-white .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .disabled-select-white .ant-select-arrow {
            color: rgba(0, 0, 0, 0.25) !important;
          }
        `}
      </style>
      <div className="min-vh-100 bg-light d-flex flex-column">
        {/* Header */}
        <div
          className="text-white py-0 px-4 d-flex align-items-center"
          style={{ backgroundColor: "#040c4b" }}
        >
          <div className="logo-wrapper">
            <img
              className=""
              src="/AcademyLogoBlue.png"
              style={{
                filter: "invert(1) grayscale(1) brightness(2)",
                width: "55px",
                height: "auto",
              }}
              alt="Al Hilal Academy Logo"
            />
          </div>
          <h5 className="mb-0 fw-bold text-white">Al Hilal Academy</h5>
        </div>

        {/* Content */}
        <div className="flex-grow-1 d-flex justify-content-center align-items-center pt-5 pb-4">
          <div
            className="bg-white shadow p-4"
            style={{ maxWidth: 500, width: "100%", borderRadius: "1rem" }}
          >
            {isPaymentCompleted ? (
              <>
                {/* Success Icon and Message */}
                <div className="text-success mb-2">
                  <svg
                    width="60"
                    height="60"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="mx-auto"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22,4 12,14.01 9,11.01"></polyline>
                  </svg>
                </div>

                <h4 className="fw-bold mb-2 text-success">Payment Already Completed!</h4>
                <p className="text-muted mb-3">
                  This payment has already been processed successfully.
                </p>

                {/* Payment Details Display */}
                <div className="card border-0 bg-light mb-3">
                  <div className="card-body p-4">
                    <h6 className="card-title text-primary mb-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="me-2"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      Payment Details
                    </h6>
                    <div className="row g-2">
                      {/* Transaction ID */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Transaction ID:</span>
                          <span className="fw-semibold font-monospace small">{paymentDetails?.referenceNumber || 'N/A'}</span>
                        </div>
                      </div>
                      {/* Transaction Date */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Date:</span>
                          <span className="fw-semibold">
                            {paymentDetails?.transactionDate
                              ? new Date(paymentDetails.transactionDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                              : new Date().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            }
                          </span>
                        </div>
                      </div>
                      {/* Amount Paid */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Amount Paid:</span>
                          <span className="fw-semibold text-success">
                            {paymentDetails?.currency || currencyCode} {paymentDetails?.amount
                              ? parseFloat(paymentDetails.amount).toFixed(2)
                              : '0.00'
                            }
                          </span>
                        </div>
                      </div>
                      <hr className="my-2" />
                      {/* Player Name */}
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted small">Player Name:</span>
                          <span className="fw-semibold">{player?.fullName || player?.localizedFullName || paymentDetails?.playerName || 'N/A'}</span>
                        </div>
                      </div>
                      {/* Type - Show for Product payments */}
                      {paymentType === 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Type:</span>
                            <span className="fw-semibold">Product Purchase</span>
                          </div>
                        </div>
                      )}
                      {/* Product Items - Show for Product payments */}
                      {paymentType === 'Product' && productItems && productItems.length > 0 && (
                        <div className="col-12">
                          <div className="py-1">
                            <span className="text-muted small">Items:</span>
                            <div className="mt-1">
                              {productItems.map((item, index) => (
                                <div key={index} className="d-flex justify-content-between align-items-center py-1 ps-2">
                                  <span className="fw-semibold small">
                                    {item.productName}{item.sizeName ? ` (${item.sizeName})` : ''}{item.quantity > 1 ? ` x${item.quantity}` : ''}
                                  </span>
                                  <span className="text-muted small">{currencyCode} {(item.totalPrice || 0).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Membership - Hide for Product payments */}
                      {paymentType !== 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Membership:</span>
                            <span className="fw-semibold">{paymentDetails?.membershipName || selectedMembership?.label || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                      {/* Start Date - Hide for Product payments */}
                      {paymentType !== 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Start Date:</span>
                            <span className="fw-semibold">
                              {paymentDetails?.startDate
                                ? new Date(paymentDetails.startDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                                : 'N/A'
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Expiry Date - Hide for Product payments */}
                      {paymentType !== 'Product' && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Expiry Date:</span>
                            <span className="fw-semibold">
                              {paymentDetails?.expiryDate
                                ? new Date(paymentDetails.expiryDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })
                                : 'N/A'
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      {/* Branch Name */}
                      {paymentDetails?.branchName && (
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-1">
                            <span className="text-muted small">Branch:</span>
                            <span className="fw-semibold">{paymentDetails.branchName}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Download Receipt Button */}
                <div className="mb-2">
                  <button
                    className="btn text-white w-100"
                    style={{ backgroundColor: "rgb(4, 12, 75)" }}
                    onClick={handleDownloadReceipt}
                    disabled={downloadingReceipt}
                  >
                    {downloadingReceipt ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          style={{ width: "1rem", height: "1rem" }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Generating PDF...
                      </>
                    ) : (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="me-2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7,10 12,15 17,10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Download Receipt PDF
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : !showPaymentForm && !showPaymentMethodSelection ? (
              <>
                <h4 className="text-center fw-bold mb-2">Player Payment</h4>
                <p className="text-center text-muted mb-4">Complete your payment</p>

                {/* Profile Image */}
                <div className="d-flex justify-content-center mb-3">
                  <div
                    className="profile-img mx-auto rounded-circle bg-white"
                    style={{ width: "80px", height: "80px", overflow: "hidden" }}
                  >
                    <img
                      className="player-profile-img img-fluid"
                      src={
                        player?.player_image
                          ? player.player_image
                          : process.env.PUBLIC_URL + "/NoImageProfile.png"
                      }
                      alt="Player Profile"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                </div>
                <h5 className="text-center fw-bold mb-1">{player?.fullName || player?.localizedFullName || "N/A"}</h5>
                <p className="text-center text-muted mb-3">
                  Player ID: {player?.playerId || "N/A"}
                </p>

                <hr />
                <div className="row text-start mb-3">
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#666"
                        strokeWidth="2"
                        style={{ marginRight: "10px" }}
                      >
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-8 8-8s8 4 8 8" />
                      </svg>
                      <div>
                        <small className="text-muted">Age Group</small>
                        <br />
                        <strong>{paymentDetails?.ageGroupName || player?.currentMembership?.ageGroupName || "N/A"}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#666"
                        strokeWidth="2"
                        style={{ marginRight: "10px" }}
                      >
                        <path d="M17 3a4 4 0 0 0-4 4h-2a4 4 0 0 0-4-4H3v18h18V7a4 4 0 0 0-4-4z" />
                      </svg>
                      <div>
                        <small className="text-muted">Team</small>
                        <br />
                        <strong>{paymentDetails?.teamName || player?.currentMembership?.teamName || "N/A"}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#666"
                        strokeWidth="2"
                        style={{ marginRight: "10px" }}
                      >
                        <rect x="3" y="4" width="18" height="14" rx="2" />
                        <path d="M3 10h18" />
                        <path d="M9 14l2 2 4-4" />
                      </svg>
                      <div>
                        <small className="text-muted">Session</small>
                        <br />
                        <strong>{paymentDetails?.sessionName || player?.currentMembership?.sessionName || "N/A"}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="col-6 mb-3">
                    <div className="d-flex align-items-center">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#666"
                        strokeWidth="2"
                        style={{ marginRight: "10px" }}
                      >
                        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                        <path d="M3 6h18" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                      </svg>
                      <div>
                        <small className="text-muted">Branch</small>
                        <br />
                        <strong>{paymentDetails?.branchName || player?.currentMembership?.branchName || "N/A"}</strong>
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                {/* Membership Section - Hide for Product and Transfer payment types */}
                {paymentType !== 'Product' && paymentType !== 'Transfer' && (
                  <div className="mb-3">
                    <h6 className="fw-bold mb-2">{isAllowManualMembershipSelection ? 'Select Membership' : 'Membership'}</h6>
                    <Select
                      value={selectedMembership?.value}
                      style={{
                        width: "100%"
                      }}
                      className={!isAllowManualMembershipSelection ? "disabled-select-white" : ""}
                      onChange={handleMembershipChange}
                      loading={loading}
                      disabled={!isAllowManualMembershipSelection}
                      open={isAllowManualMembershipSelection ? undefined : false}
                    >
                      {membershipOptions.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label} {option.localizedLabel && `(${option.localizedLabel})`} - {currencyCode} {option.price.toFixed(2)}
                        </Option>
                      ))}
                    </Select>
                  </div>
                )}

                {/* Membership Section for Transfer - Read-only */}
                {paymentType === 'Transfer' && (
                  <div className="mb-3">
                    <h6 className="fw-bold mb-2">Membership</h6>
                    <div className="p-3 rounded-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e5e7eb' }}>
                      <div className="fw-semibold">
                        {selectedMembership?.label} {selectedMembership?.localizedLabel && `(${selectedMembership.localizedLabel})`}
                      </div>
                      <div className="text-muted small mt-1">
                        {currencyCode} {(selectedMembership?.price || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Discount Section - Hide for Product and Transfer payment types - Show if:
                  1. Discounts are available for this membership
                  2. AND either:
                     - User has manually changed membership, OR
                     - No discount was pre-applied (no onlineDiscountId)
                  3. AND no hidden discount is active
              */}
                {/* Promo Code Section - shown for Membership payments from mobile/website only */}
                {paymentType !== 'Product' && paymentType !== 'Transfer' && (paymentDetails.source === 'mobile' || paymentDetails.source === 'app' || paymentDetails.source === 'website') && (
                  <div className="mb-3">
                    <h6 className="fw-bold mb-2">Have a Promo Code?</h6>
                    {promoApplied && selectedDiscount?.isPromo ? (
                      <div
                        className="d-flex align-items-center justify-content-between p-3 rounded-3"
                        style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
                      >
                        <div>
                          <div className="fw-semibold text-success" style={{ fontSize: '0.9rem' }}>
                            ✓ Promo Applied: {selectedDiscount.label}
                          </div>
                          <div className="text-success" style={{ fontSize: '0.85rem' }}>
                            {selectedDiscount.type === 'percentage'
                              ? `${selectedDiscount.discountValue}% off`
                              : `${currencyCode} ${selectedDiscount.discountValue} off`}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={handleRemovePromo}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex gap-2">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter promo code"
                            value={promoCode}
                            onChange={(e) => {
                              setPromoCode(e.target.value);
                              setPromoError('');
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromoCode(); }}
                            disabled={isApplyingPromo}
                            style={{ borderRadius: '8px' }}
                          />
                          <button
                            type="button"
                            className="btn fw-semibold"
                            onClick={handleApplyPromoCode}
                            disabled={isApplyingPromo || !promoCode.trim()}
                            style={{ backgroundColor: '#040c4b', color: 'white', borderRadius: '8px', minWidth: '80px' }}
                          >
                            {isApplyingPromo ? (
                              <span className="spinner-border spinner-border-sm" role="status" />
                            ) : 'Apply'}
                          </button>
                        </div>
                        {promoError && (
                          <div className="text-danger mt-1" style={{ fontSize: '0.85rem' }}>
                            {promoError}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {paymentType !== 'Product' && paymentType !== 'Transfer' && discountOptions.length > 0 && (hasMembershipChanged || !paymentDetails.onlineDiscountId || paymentDetails.onlineDiscountId === 0) && !hiddenDiscount && !selectedDiscount?.isPromo && (
                  <div className="mb-3">
                    <h6 className="fw-bold mb-2">Select Additional Discount</h6>
                    <div className="row g-2">
                      {discountOptions.map((option) => (
                        <div key={option.value} className="col-6">
                          <div
                            className={`discount-card p-3 rounded-3 cursor-pointer ${selectedDiscount?.value === option.value
                                ? 'selected-discount'
                                : ''
                              }`}
                            onClick={() => handleDiscountChange(option.value)}
                            style={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div className="d-flex flex-column h-100">
                              <h6 className="fw-bold mb-1">
                                {option.label}
                              </h6>
                              {option.description && (
                                <p className="small mb-2 flex-grow-1" style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>
                                  {option.description}
                                </p>
                              )}
                              <div className="fw-bold discount-value">
                                {option.type === 'percentage' ? `${option.discountValue}%` : `${currencyCode} ${option.discountValue}`} off
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <hr />

                {/* Detailed Pricing Breakdown */}
                <div className="pricing-breakdown mb-4">
                  {/* Product Payment Type Pricing */}
                  {paymentType === 'Product' && (
                    <>
                      {/* Product Items List */}
                      {productItems && productItems.length > 0 && (
                        <div className="mb-3">
                          {productItems.map((item, index) => (
                            <div key={index} className="d-flex justify-content-between mb-1">
                              <span className="text-muted">
                                {item.productName}{item.sizeName ? ` (${item.sizeName})` : ''}{item.quantity > 1 ? ` x${item.quantity}` : ''}
                              </span>
                              <span className="fw-bold">{currencyCode} {(item.totalPrice || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Product Price */}
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Product Price:</span>
                        <span className="fw-bold">
                          {currencyCode} {(productPrice || 0).toFixed(2)}
                        </span>
                      </div>

                      {/* Product VAT - Only show if VAT is greater than 0 */}
                      {productVat > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted">Product VAT ({(productVatPercentage || 0).toFixed(1)}%):</span>
                          <span className="fw-bold">
                            {currencyCode} {(productVat || 0).toFixed(2)}
                          </span>
                        </div>
                      )}

                      <hr style={{ margin: "12px 0", borderWidth: "2px" }} />

                      {/* Product Total */}
                      <div className="d-flex justify-content-between mb-3">
                        <span className="fw-bold" style={{ fontSize: "1.1em" }}>Product Total:</span>
                        <span className="fw-bold fs-5" style={{ color: "#040c4b" }}>
                          {currencyCode} {((productPrice || 0) + (productVat || 0)).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Membership Payment Type Pricing */}
                  {paymentType !== 'Product' && (
                    <>
                      {/* For Transfer type - show detailed pricing breakdown */}
                      {paymentType === 'Transfer' ? (
                        <>
                          {/* Membership Price */}
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Membership Price:</span>
                            <span className="fw-bold">
                              {currencyCode === 'SAR' ? 'ریال' : currencyCode} {(selectedMembership?.price || 0).toFixed(2)}
                            </span>
                          </div>

                          {/* Discount (incl. VAT): list price with VAT minus the stored total before points.
                              The amounts below come from the payment log - what checkout charges. */}
                          {(() => {
                            const listWithVat = (selectedMembership?.price || 0) * (companySettings?.isTaxEnabled !== false ? 1.15 : 1);
                            const discount = listWithVat - getTransferGrossTotal();
                            return discount > 0.01 ? (
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-success">Discount (incl. VAT):</span>
                                <span className="text-success fw-bold">
                                  -{currencyCode === 'SAR' ? 'ریال' : currencyCode} {discount.toFixed(2)}
                                </span>
                              </div>
                            ) : null;
                          })()}

                          {/* Total Amount (before points, after discount, incl. VAT) */}
                          <div className="d-flex justify-content-between mb-2" style={{
                            padding: '8px 0'
                          }}>
                            <span className="fw-bold" style={{ fontSize: '1.05em', color: '#1976d2' }}>Total Amount (incl. VAT):</span>
                            <span className="fw-bold" style={{ fontSize: '1.15em', color: '#1976d2' }}>
                              {currencyCode === 'SAR' ? 'ریال' : currencyCode} {getTransferGrossTotal().toFixed(2)}
                            </span>
                          </div>

                          <hr style={{ margin: "8px 0" }} />

                          {/* Remaining Points (if player has points before using them) */}
                          {(player?.points + (paymentDetails?.pointsUsed || 0)) > 0 && (
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.95em" }}>
                              <span className="text-success">Remaining Points:</span>
                              <span className="text-success fw-semibold">
                                {currencyCode === 'SAR' ? 'نقاط' : 'Points'} {((player?.points || 0) + (paymentDetails?.pointsUsed || 0)).toFixed(2)}
                              </span>
                            </div>
                          )}

                          {/* Points Used (if points were used) */}
                          {paymentDetails?.pointsUsed > 0 && (
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.95em" }}>
                              <span className="text-danger">Points Used:</span>
                              <span className="text-danger fw-semibold">
                                -{currencyCode === 'SAR' ? 'نقاط' : 'Points'} {(paymentDetails.pointsUsed || 0).toFixed(2)}
                              </span>
                            </div>
                          )}

                          <hr style={{ margin: "12px 0", borderWidth: "2px" }} />

                          {/* Final Total - Amount to Pay (Total Amount - Points Used) */}
                          <div className="d-flex justify-content-between mb-3">
                            <span className="fw-bold" style={{ fontSize: "1.1em" }}>Final Total:</span>
                            <span className="fw-bold fs-5" style={{ color: "#040c4b" }}>
                              {currencyCode === 'SAR' ? 'ریال' : currencyCode} {getTransferAmountToPay().toFixed(2)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Original Membership Price */}
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Original Membership Price:</span>
                            <span className="fw-bold">
                              {currencyCode} {(selectedMembership?.price || 0).toFixed(2)}
                            </span>
                          </div>

                          {/* Show discount only if selectedDiscount or hiddenDiscount is available for current membership */}
                          {(selectedDiscount || hiddenDiscount) && (
                            <>
                              <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.9em" }}>
                                <span className="text-success">
                                  - Membership Discount ({(selectedDiscount || hiddenDiscount).label}
                                  {(selectedDiscount || hiddenDiscount).type === 'percentage'
                                    ? ` ${(selectedDiscount || hiddenDiscount).discountValue}%`
                                    : ` ${currencyCode} ${(selectedDiscount || hiddenDiscount).discountValue}`}):
                                </span>
                                <span className="text-success">
                                  -{currencyCode} {(() => {
                                    let membershipAmount = selectedMembership?.price || 0;
                                    let discountAmount = 0;
                                    const activeDiscount = selectedDiscount || hiddenDiscount;
                                    if (activeDiscount.type === 'percentage') {
                                      discountAmount = membershipAmount * (activeDiscount.discountValue / 100);
                                    } else if (activeDiscount.type === 'fixed') {
                                      discountAmount = Math.min(membershipAmount, activeDiscount.discountValue);
                                    }
                                    return discountAmount.toFixed(2);
                                  })()}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Price after membership discount:</span>
                                <span className="fw-semibold">
                                  {currencyCode} {(() => {
                                    let membershipAmount = selectedMembership?.price || 0;
                                    const activeDiscount = selectedDiscount || hiddenDiscount;
                                    // Apply online discount
                                    if (activeDiscount.type === 'percentage') {
                                      membershipAmount = membershipAmount * (1 - activeDiscount.discountValue / 100);
                                    } else if (activeDiscount.type === 'fixed') {
                                      membershipAmount = Math.max(0, membershipAmount - activeDiscount.discountValue);
                                    }
                                    return membershipAmount.toFixed(2);
                                  })()}
                                </span>
                              </div>
                            </>
                          )}

                          {/* Final Membership Price after all discounts */}
                          <div className="d-flex justify-content-between mb-2 border-top pt-2">
                            <span className="text-muted fw-semibold">Final Membership Price:</span>
                            <span className="fw-bold text-primary">
                              {currencyCode} {(() => {
                                let membershipAmount = selectedMembership?.price || 0;
                                // Apply selected discount or hidden discount if available
                                const activeDiscount = selectedDiscount || hiddenDiscount;
                                if (activeDiscount) {
                                  if (activeDiscount.type === 'percentage') {
                                    membershipAmount = membershipAmount * (1 - activeDiscount.discountValue / 100);
                                  } else if (activeDiscount.type === 'fixed') {
                                    membershipAmount = Math.max(0, membershipAmount - activeDiscount.discountValue);
                                  }
                                }
                                return membershipAmount.toFixed(2);
                              })()}
                            </span>
                          </div>

                          {/* Total Savings */}
                          {(selectedDiscount || hiddenDiscount) && (
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.9em" }}>
                              <span className="text-success fw-semibold">Total Savings:</span>
                              <span className="text-success fw-semibold">
                                {currencyCode} {(() => {
                                  const originalPrice = selectedMembership?.price || 0;
                                  let finalPrice = originalPrice;
                                  const activeDiscount = selectedDiscount || hiddenDiscount;

                                  // Apply active discount
                                  if (activeDiscount.type === 'percentage') {
                                    finalPrice = finalPrice * (1 - activeDiscount.discountValue / 100);
                                  } else if (activeDiscount.type === 'fixed') {
                                    finalPrice = Math.max(0, finalPrice - activeDiscount.discountValue);
                                  }

                                  const totalSavings = originalPrice - finalPrice;
                                  return totalSavings.toFixed(2);
                                })()}
                              </span>
                            </div>
                          )}

                          {/* Bundle Cost (if applicable) */}
                          {paymentDetails.addClothes && paymentDetails.bundleAmount > 0 && (
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Bundle:</span>
                              <span className="fw-bold">{currencyCode} {paymentDetails.bundleAmount.toFixed(2)}</span>
                            </div>
                          )}

                          {/* Discount Percentage */}
                          {(selectedDiscount || hiddenDiscount) && (
                            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.9em" }}>
                              <span className="text-success">Discount %:</span>
                              <span className="text-success">
                                {(() => {
                                  let totalDiscountPercentage = 0;
                                  const activeDiscount = selectedDiscount || hiddenDiscount;

                                  // Add online discount if selected and it's a percentage
                                  if (activeDiscount.type === 'percentage') {
                                    totalDiscountPercentage = activeDiscount.discountValue;
                                  }

                                  // For fixed amount discounts, calculate the percentage
                                  if (activeDiscount.type === 'fixed') {
                                    const originalPrice = selectedMembership?.price || 0;
                                    if (originalPrice > 0) {
                                      totalDiscountPercentage = (activeDiscount.discountValue / originalPrice) * 100;
                                    }
                                  }

                                  return `${totalDiscountPercentage.toFixed(1)}%`;
                                })()}
                              </span>
                            </div>
                          )}

                          <hr style={{ margin: "12px 0" }} />

                          {/* Membership VAT - Only show if tax is enabled */}
                          {(companySettings?.isTaxEnabled !== false) && (
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Membership VAT (15.0%):</span>
                              <span className="fw-bold">
                                {currencyCode} {(() => {
                                  let membershipAmount = selectedMembership?.price || 0;

                                  // Apply existing discount from payment details
                                  if (paymentDetails.discountPercent > 0) {
                                    membershipAmount = membershipAmount * (1 - paymentDetails.discountPercent / 100);
                                  }

                                  // Apply selected online discount or hidden discount
                                  const activeDiscount = selectedDiscount || hiddenDiscount;
                                  if (activeDiscount) {
                                    if (activeDiscount.type === 'percentage') {
                                      membershipAmount = membershipAmount * (1 - activeDiscount.discountValue / 100);
                                    } else if (activeDiscount.type === 'fixed') {
                                      membershipAmount = Math.max(0, membershipAmount - activeDiscount.discountValue);
                                    }
                                  }

                                  let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
                                  let subtotal = membershipAmount + bundleCost;
                                  let vatAmount = subtotal * 0.15;
                                  return vatAmount.toFixed(2);
                                })()}
                              </span>
                            </div>
                          )}

                          <hr style={{ margin: "12px 0", borderWidth: "2px" }} />

                          {/* Membership Total (before points) */}
                          <div className="d-flex justify-content-between mb-2">
                            <span className="fw-bold" style={{ fontSize: "1.1em" }}>Membership Total:</span>
                            <span className="fw-bold fs-5" style={{ color: "#040c4b" }}>
                              {currencyCode} {(() => {
                                // Calculate total without points deduction for display
                                let membershipAmount = selectedMembership?.price || 0;
                                const activeDiscount = selectedDiscount || hiddenDiscount;
                                if (activeDiscount) {
                                  if (activeDiscount.type === 'percentage') {
                                    membershipAmount = membershipAmount * (1 - activeDiscount.discountValue / 100);
                                  } else if (activeDiscount.type === 'fixed') {
                                    membershipAmount = Math.max(0, membershipAmount - activeDiscount.discountValue);
                                  }
                                }
                                let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
                                let subtotal = membershipAmount + bundleCost;
                                const isTaxEnabled = companySettings?.isTaxEnabled !== false;
                                return (isTaxEnabled ? subtotal * 1.15 : subtotal).toFixed(2);
                              })()}
                            </span>
                          </div>

                          {/* Points Applied row */}
                          {applyWalletPoints && (player?.walletPoints?.balancePoints || 0) > 0 && (
                            <>
                              <div className="d-flex justify-content-between mb-2" style={{ fontSize: "0.95em" }}>
                                <span>Points Applied:</span>
                                <span className="fw-semibold">
                                  -{currencyCode} {Math.min(player.walletPoints.balancePoints, (() => {
                                    let membershipAmount = selectedMembership?.price || 0;
                                    const activeDiscount = selectedDiscount || hiddenDiscount;
                                    if (activeDiscount) {
                                      if (activeDiscount.type === 'percentage') membershipAmount = membershipAmount * (1 - activeDiscount.discountValue / 100);
                                      else if (activeDiscount.type === 'fixed') membershipAmount = Math.max(0, membershipAmount - activeDiscount.discountValue);
                                    }
                                    let bundleCost = (paymentDetails.addClothes && paymentDetails.bundleAmount) ? paymentDetails.bundleAmount : 0;
                                    let subtotal = membershipAmount + bundleCost;
                                    const isTaxEnabled = companySettings?.isTaxEnabled !== false;
                                    return isTaxEnabled ? subtotal * 1.15 : subtotal;
                                  })()).toFixed(2)}
                                </span>
                              </div>
                              <hr style={{ margin: "8px 0", borderWidth: "2px" }} />
                              <div className="d-flex justify-content-between mb-3">
                                <span className="fw-bold" style={{ fontSize: "1.1em" }}>Amount to Pay:</span>
                                <span className="fw-bold fs-5" style={{ color: "#040c4b" }}>
                                  {currencyCode} {calculateTotalAmount().toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* Wallet Points Checkbox - Membership type only, when player has points */}
                {paymentType !== 'Product' && paymentType !== 'Transfer' && (player?.walletPoints?.balancePoints || 0) > 0 && (
                  <div className="mb-3">
                    <div className="form-check d-flex align-items-start p-3 rounded-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                      <input
                        className="form-check-input mt-1"
                        type="checkbox"
                        id="walletPointsCheckbox"
                        checked={applyWalletPoints}
                        onChange={(e) => setApplyWalletPoints(e.target.checked)}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          accentColor: "#16a34a",
                          marginRight: "10px",
                          flexShrink: 0
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="walletPointsCheckbox"
                        style={{ cursor: "pointer", fontSize: "14px", lineHeight: "1.5", color: "#166534" }}
                      >
                        <span style={{ fontWeight: 600 }}>Apply wallet points</span>
                        <br />
                        <span style={{ fontSize: "12px", color: "#15803d" }}>
                          Available: {(player.walletPoints.balancePoints).toFixed(2)} points (= {currencyCode} {(player.walletPoints.balancePoints).toFixed(2)})
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Terms and Conditions Checkbox */}
                <div className="mb-3">
                  <div className="form-check d-flex align-items-start">
                    <input
                      className="form-check-input mt-1"
                      type="checkbox"
                      id="termsCheckbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        accentColor: "#040c4b",
                        marginRight: "10px",
                        flexShrink: 0
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="termsCheckbox"
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        lineHeight: "1.5",
                        color: "#374151"
                      }}
                    >
                      I accept the{" "}
                      <a
                        href="https://www.alhilalacademy.com/policies/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#040c4b",
                          fontWeight: "bold",
                          textDecoration: "none",
                          borderBottom: "1px solid #040c4b"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms and Conditions
                      </a>
                      {" "}and{" "}
                      <a
                        href="https://www.alhilalacademy.com/policies/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#040c4b",
                          fontWeight: "bold",
                          textDecoration: "none",
                          borderBottom: "1px solid #040c4b"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                    </label>
                  </div>
                </div>

                <div className="row gx-3">
                  <div className="col">
                    <button
                      className="btn btn-outline-secondary w-100 rounded"
                      onClick={handleCancel}
                    >
                      ✗ Cancel
                    </button>
                  </div>
                  <div className="col">
                    <button
                      className="btn w-100 text-white rounded"
                      style={{
                        backgroundColor: termsAccepted ? "#040c4b" : "#9ca3af",
                        cursor: termsAccepted ? "pointer" : "not-allowed",
                        opacity: termsAccepted ? 1 : 0.6
                      }}
                      onClick={handlePayNow}
                      disabled={loading || !termsAccepted}
                      title={!termsAccepted ? "Please accept the terms and conditions to continue" : ""}
                    >
                      ✔ Pay Now
                    </button>
                  </div>
                </div>
              </>
            ) : null}

            {/* Payment Method Selection */}
            {showPaymentMethodSelection && checkoutId && !showPaymentForm && (
              <>
                <h6 className="fw-bold mb-3 text-center">Select Payment Method</h6>

                {/* Payment Method Grid */}
                <div className="row g-3 mb-4">

                  {/* Mastercard */}
                  <div className="col-6">
                    <label
                      className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                      style={{
                        cursor: "pointer",
                        borderColor: selectedPaymentMethod === 'mastercard' ? "#040c4b" : "#e5e7eb",
                        backgroundColor: selectedPaymentMethod === 'mastercard' ? "#f8fafc" : "white",
                        transition: "all 0.2s ease",
                        minHeight: "100px",
                        maxHeight: "100px",
                        borderWidth: "2px"
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPaymentMethod !== 'mastercard') {
                          e.target.style.borderColor = "#cbd5e1";
                          e.target.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPaymentMethod !== 'mastercard') {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="mastercard"
                        checked={selectedPaymentMethod === 'mastercard'}
                        onChange={() => setSelectedPaymentMethod('mastercard')}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          width: "18px",
                          height: "18px",
                          accentColor: "#040c4b",
                          zIndex: 10
                        }}
                      />
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                        <img
                          src="/master.png"
                          alt="Mastercard"
                          style={{
                            height: "50px",
                            width: "auto",
                            objectFit: "contain"
                          }}
                        />
                      </div>
                    </label>
                  </div>

                  {/* Apple Pay - Only show on HTTPS - MOVED TO FIRST POSITION */}
                  {window.location.protocol === 'https:' && (
                    <div className="col-6">
                      <label
                        className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                        style={{
                          cursor: "pointer",
                          borderColor: selectedPaymentMethod === 'apple-pay' ? "#040c4b" : "#e5e7eb",
                          backgroundColor: selectedPaymentMethod === 'apple-pay' ? "#f8fafc" : "white",
                          transition: "all 0.2s ease",
                          minHeight: "100px",
                          maxHeight: "100px",
                          borderWidth: "2px",
                          boxShadow: selectedPaymentMethod === 'apple-pay' ? "0 4px 12px rgba(4,12,75,0.15)" : "none"
                        }}
                        onMouseEnter={(e) => {
                          if (selectedPaymentMethod !== 'apple-pay') {
                            e.target.style.borderColor = "#cbd5e1";
                            e.target.style.backgroundColor = "#f9fafb";
                            e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedPaymentMethod !== 'apple-pay') {
                            e.target.style.borderColor = "#e5e7eb";
                            e.target.style.backgroundColor = "white";
                            e.target.style.boxShadow = "none";
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="apple-pay"
                          checked={selectedPaymentMethod === 'apple-pay'}
                          onChange={() => {
                            setSelectedPaymentMethod('apple-pay');
                            // Immediately create Apple Pay widget when selected
                            setTimeout(() => {
                              createApplePayWidgetInline();
                            }, 100);
                          }}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            width: "18px",
                            height: "18px",
                            accentColor: "#040c4b",
                            zIndex: 10
                          }}
                        />
                        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "100%" }}>
                          <img
                            src="/apple-pay.png"
                            alt="Apple Pay"
                            style={{
                              height: "50px",
                              width: "auto",
                              objectFit: "contain",
                              marginBottom: "4px"
                            }}
                          />
                          <small className="text-muted text-center" style={{ fontSize: "11px", lineHeight: "1.2" }}>
                            Instant Pay
                          </small>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* MADA */}
                  <div className="col-6">
                    <label
                      className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                      style={{
                        cursor: "pointer",
                        borderColor: selectedPaymentMethod === 'mada' ? "#040c4b" : "#e5e7eb",
                        backgroundColor: selectedPaymentMethod === 'mada' ? "#f8fafc" : "white",
                        transition: "all 0.2s ease",
                        minHeight: "100px",
                        maxHeight: "100px",
                        borderWidth: "2px"
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPaymentMethod !== 'mada') {
                          e.target.style.borderColor = "#cbd5e1";
                          e.target.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPaymentMethod !== 'mada') {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="mada"
                        checked={selectedPaymentMethod === 'mada'}
                        onChange={() => setSelectedPaymentMethod('mada')}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          width: "18px",
                          height: "18px",
                          accentColor: "#040c4b",
                          zIndex: 10
                        }}
                      />
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                        <img
                          src="/mada.png"
                          alt="MADA"
                          style={{
                            height: "50px",
                            width: "auto",
                            objectFit: "contain"
                          }}
                        />
                      </div>
                    </label>
                  </div>

                  {/* VISA */}
                  <div className="col-6">
                    <label
                      className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                      style={{
                        cursor: "pointer",
                        borderColor: selectedPaymentMethod === 'visa' ? "#040c4b" : "#e5e7eb",
                        backgroundColor: selectedPaymentMethod === 'visa' ? "#f8fafc" : "white",
                        transition: "all 0.2s ease",
                        minHeight: "100px",
                        maxHeight: "100px",
                        borderWidth: "2px"
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPaymentMethod !== 'visa') {
                          e.target.style.borderColor = "#cbd5e1";
                          e.target.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPaymentMethod !== 'visa') {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="visa"
                        checked={selectedPaymentMethod === 'visa'}
                        onChange={() => setSelectedPaymentMethod('visa')}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          width: "18px",
                          height: "18px",
                          accentColor: "#040c4b",
                          zIndex: 10
                        }}
                      />
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                        <img
                          src="/visa.png"
                          alt="Visa"
                          style={{
                            height: "50px",
                            width: "auto",
                            objectFit: "contain"
                          }}
                        />
                      </div>
                    </label>
                  </div>

                  {/* TAMARA */}
                  <div className="col-6">
                    <label
                      className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                      style={{
                        cursor: "pointer",
                        borderColor: selectedPaymentMethod === 'tamara' ? "#040c4b" : "#e5e7eb",
                        backgroundColor: selectedPaymentMethod === 'tamara' ? "#f8fafc" : "white",
                        transition: "all 0.2s ease",
                        minHeight: "100px",
                        maxHeight: "100px",
                        borderWidth: "2px"
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPaymentMethod !== 'tamara') {
                          e.target.style.borderColor = "#cbd5e1";
                          e.target.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPaymentMethod !== 'tamara') {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="tamara"
                        checked={selectedPaymentMethod === 'tamara'}
                        onChange={() => setSelectedPaymentMethod('tamara')}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          width: "18px",
                          height: "18px",
                          accentColor: "#040c4b",
                          zIndex: 10
                        }}
                      />
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                        <img
                          src="/tamara.jpg"
                          alt="Tamara"
                          style={{
                            height: "50px",
                            width: "auto",
                            objectFit: "contain"
                          }}
                        />
                      </div>
                    </label>
                  </div>

                  {/* Tabby */}
                  <div className="col-6">
                    <label
                      className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                      style={{
                        cursor: "pointer",
                        borderColor: selectedPaymentMethod === 'tabby' ? "#040c4b" : "#e5e7eb",
                        backgroundColor: selectedPaymentMethod === 'tabby' ? "#f8fafc" : "white",
                        transition: "all 0.2s ease",
                        minHeight: "100px",
                      }}
                      onClick={() => handlePaymentMethodSelect('tabby')}
                      onMouseEnter={(e) => {
                        if (selectedPaymentMethod !== 'tabby') {
                          e.target.style.borderColor = "#040c4b";
                          e.target.style.backgroundColor = "#f8fafc";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPaymentMethod !== 'tabby') {
                          e.target.style.borderColor = "#e5e7eb";
                          e.target.style.backgroundColor = "white";
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="tabby"
                        checked={selectedPaymentMethod === 'tabby'}
                        onChange={() => setSelectedPaymentMethod('tabby')}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          width: "18px",
                          height: "18px",
                          accentColor: "#040c4b",
                          zIndex: 10
                        }}
                      />
                      <div className="d-flex align-items-center justify-content-center" style={{ height: "100%" }}>
                        <img
                          src="/tabby.png"
                          alt="Tabby"
                          style={{
                            height: "50px",
                            width: "auto",
                            objectFit: "contain"
                          }}
                        />
                      </div>
                    </label>
                  </div>

                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2 mt-4">
                  {/* Apple Pay Widget - Show HyperPay's Apple Pay button when selected */}
                  {selectedPaymentMethod === 'apple-pay' && (
                    <div className="text-center mb-3">
                      <div
                        className="apple-pay-inline-wrapper d-flex align-items-center justify-content-center position-relative"
                        style={{
                          padding: "16px",
                          backgroundColor: "#000000",
                          borderRadius: "12px",
                          border: "2px solid #000000",
                          minHeight: "56px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                        }}
                      >
                        {/* Loading state for Apple Pay button */}
                        <div className="apple-pay-loading d-flex align-items-center justify-content-center text-white">
                          <div
                            className="spinner-border spinner-border-sm me-2"
                            style={{ color: "#ffffff", width: "1.2rem", height: "1.2rem" }}
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: "500" }}>Loading Apple Pay...</span>
                        </div>
                        {/* HyperPay will inject the Apple Pay button here and hide the loading */}
                      </div>
                      <small className="text-muted d-block mt-2">
                        Click the Apple Pay button above to complete your payment
                      </small>
                    </div>
                  )}

                  {/* Continue Button - Show for non-Apple Pay methods */}
                  {selectedPaymentMethod && selectedPaymentMethod !== 'apple-pay' && (
                    <button
                      className="btn btn-primary"
                      style={{
                        backgroundColor: "#040c4b",
                        borderColor: "#040c4b",
                        padding: "16px",
                        fontSize: "16px",
                        fontWeight: "600",
                        borderRadius: "12px",
                        minHeight: "56px"
                      }}
                      onClick={() => handlePaymentMethodSelect(selectedPaymentMethod)}
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        <img
                          src={selectedPaymentMethod === 'mastercard' ? '/master.png' : selectedPaymentMethod === 'tamara' ? '/tamara.jpg' : `/${selectedPaymentMethod}.png`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                          alt={selectedPaymentMethod.toUpperCase()}
                          style={{
                            height: "32px",
                            width: "auto",
                            objectFit: "contain",
                            marginRight: "12px"
                          }}
                        />
                        Pay with {selectedPaymentMethod === 'mastercard' ? 'MASTERCARD' : selectedPaymentMethod.toUpperCase()}
                      </div>
                    </button>
                  )}

                  {/* Default message when no payment method selected */}
                  {!selectedPaymentMethod && (
                    <button
                      className="btn btn-primary"
                      style={{
                        backgroundColor: "#6b7280",
                        borderColor: "#6b7280",
                        padding: "16px",
                        fontSize: "16px",
                        fontWeight: "600",
                        borderRadius: "12px",
                        minHeight: "56px"
                      }}
                      disabled={true}
                    >
                      Select a payment method to continue
                    </button>
                  )}

                  {/* Back Button */}
                  <button
                    className="btn btn-outline-secondary"
                    style={{
                      padding: "12px",
                      fontSize: "14px",
                      borderRadius: "8px",
                      borderColor: "#6b7280",
                      color: "#6b7280",
                      backgroundColor: "transparent"
                    }}
                    onClick={() => {
                      setSelectedPaymentMethod(null);
                      setShowPaymentMethodSelection(false);
                      setCheckoutId(null);
                      cleanupHyperPayScripts();
                    }}
                  >
                    ← Back to Details
                  </button>
                </div>
              </>
            )}

            {/* MyFatoorah Payment Method Selection */}
            {showPaymentMethodSelection && myFatoorahPaymentMethods.length > 0 && !checkoutId && (
              <>
                <h6 className="fw-bold mb-3 text-center">Select Payment Method</h6>

                {/* MyFatoorah Payment Methods Grid */}
                <div className="row g-3 mb-4">
                  {myFatoorahPaymentMethods.map((method) => (
                    <div className="col-6" key={method.PaymentMethodId}>
                      <label
                        className="d-flex flex-column align-items-center justify-content-center p-3 border rounded position-relative"
                        style={{
                          cursor: "pointer",
                          borderColor: selectedMyFatoorahMethod?.PaymentMethodId === method.PaymentMethodId ? "#040c4b" : "#e5e7eb",
                          backgroundColor: selectedMyFatoorahMethod?.PaymentMethodId === method.PaymentMethodId ? "#f8fafc" : "white",
                          transition: "all 0.2s ease",
                          minHeight: "100px",
                          maxHeight: "120px",
                          borderWidth: "2px"
                        }}
                        onMouseEnter={(e) => {
                          if (selectedMyFatoorahMethod?.PaymentMethodId !== method.PaymentMethodId) {
                            e.currentTarget.style.borderColor = "#cbd5e1";
                            e.currentTarget.style.backgroundColor = "#f9fafb";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedMyFatoorahMethod?.PaymentMethodId !== method.PaymentMethodId) {
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.backgroundColor = "white";
                          }
                        }}
                      >
                        <input
                          type="radio"
                          name="myFatoorahMethod"
                          value={method.PaymentMethodId}
                          checked={selectedMyFatoorahMethod?.PaymentMethodId === method.PaymentMethodId}
                          onChange={() => setSelectedMyFatoorahMethod(method)}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            width: "18px",
                            height: "18px",
                            accentColor: "#040c4b",
                            zIndex: 10
                          }}
                        />
                        <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "100%" }}>
                          {method.ImageUrl && (
                            <img
                              src={method.ImageUrl}
                              alt={method.PaymentMethodEn}
                              style={{
                                height: "40px",
                                width: "auto",
                                objectFit: "contain",
                                marginBottom: "8px"
                              }}
                            />
                          )}
                          <span className="text-center" style={{ fontSize: "12px", fontWeight: "500" }}>
                            {method.PaymentMethodEn}
                          </span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  {/* Proceed Button */}
                  {selectedMyFatoorahMethod && (
                    <button
                      className="btn text-white"
                      style={{
                        padding: "14px",
                        fontSize: "15px",
                        fontWeight: "600",
                        backgroundColor: "#040c4b",
                        borderColor: "#040c4b",
                        borderRadius: "12px",
                        minHeight: "56px"
                      }}
                      onClick={executeMyFatoorahPayment}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2"></i>
                          Proceed with {selectedMyFatoorahMethod.PaymentMethodEn}
                        </>
                      )}
                    </button>
                  )}

                  {/* Disabled button when no method selected */}
                  {!selectedMyFatoorahMethod && (
                    <button
                      className="btn btn-secondary"
                      style={{
                        padding: "14px",
                        fontSize: "15px",
                        fontWeight: "600",
                        backgroundColor: "#e5e7eb",
                        borderColor: "#e5e7eb",
                        color: "#9ca3af",
                        borderRadius: "12px",
                        minHeight: "56px"
                      }}
                      disabled={true}
                    >
                      Select a payment method to continue
                    </button>
                  )}

                  {/* Back Button */}
                  <button
                    className="btn btn-outline-secondary"
                    style={{
                      padding: "12px",
                      fontSize: "14px",
                      borderRadius: "8px",
                      borderColor: "#6b7280",
                      color: "#6b7280",
                      backgroundColor: "transparent"
                    }}
                    onClick={() => {
                      setSelectedMyFatoorahMethod(null);
                      setShowPaymentMethodSelection(false);
                      setMyFatoorahPaymentMethods([]);
                    }}
                  >
                    ← Back to Details
                  </button>
                </div>
              </>
            )}

            {/* HyperPay Payment Form - Show after Pay Now is clicked */}
            {showPaymentForm && checkoutId && (
              <div className="payment-form-container">
                {/* Payment Method Header */}
                {selectedPaymentMethod && selectedPaymentMethod !== 'apple-pay' && (
                  <div className="d-flex align-items-center justify-content-center mb-4 p-3" style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "12px",
                    border: "2px solid #e5e7eb"
                  }}>
                    <img
                      src={selectedPaymentMethod === 'mastercard' ? '/master.png' : selectedPaymentMethod === 'tamara' ? '/tamara.jpg' : `/${selectedPaymentMethod}.png`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                      alt={selectedPaymentMethod.toUpperCase()}
                      style={{
                        height: "40px",
                        width: "auto",
                        objectFit: "contain",
                        marginRight: "12px"
                      }}
                    />
                    <div>
                      <div className="fw-bold" style={{ color: "#040c4b", fontSize: "16px" }}>
                        {selectedPaymentMethod === 'mastercard' ? 'MASTERCARD' : selectedPaymentMethod.toUpperCase()}
                      </div>
                      <small className="text-muted">{selectedPaymentMethod === 'tamara' || selectedPaymentMethod === 'tabby' ? 'Complete your payment below' : 'Enter your card details below'}</small>
                    </div>
                  </div>
                )}

                {/* Enhanced loading indicator while widget initializes */}
                {widgetLoading && (
                  <div className="text-center mb-4 p-4" style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    border: "2px solid #e5e7eb",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                  }}>
                    <div className="d-flex justify-content-center mb-3">
                      <div
                        className="spinner-border"
                        style={{ color: "#040c4b", width: "2.5rem", height: "2.5rem" }}
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                    <h6 className="fw-bold mb-2" style={{ color: "#040c4b" }}>
                      <i className={`fas ${selectedPaymentMethod === 'apple-pay' ? 'fa-mobile-alt' : 'fa-credit-card'} me-2`}></i>
                      {selectedPaymentMethod === 'apple-pay'
                        ? 'Preparing Apple Pay...'
                        : `Setting up ${selectedPaymentMethod?.toUpperCase() || 'secure'} payment form...`
                      }
                    </h6>
                    <div className="mb-3">
                      <small className="text-muted">
                        {selectedPaymentMethod === 'apple-pay'
                          ? 'Apple Pay will be ready in a moment. Your device will prompt for authentication.'
                          : 'Please wait while we load the secure payment form.'
                        }
                      </small>
                    </div>
                    {selectedPaymentMethod && selectedPaymentMethod !== 'apple-pay' && (
                      <div className="d-flex justify-content-center align-items-center">
                        <small className="text-muted me-2">Processing with:</small>
                        <div className="d-flex gap-2">
                          <img
                            src={selectedPaymentMethod === 'mastercard' ? '/master.png' : selectedPaymentMethod === 'tamara' ? '/tamara.jpg' : `/${selectedPaymentMethod}.png`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                            alt={selectedPaymentMethod.toUpperCase()}
                            style={{
                              height: "24px",
                              width: "auto",
                              objectFit: "contain"
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {selectedPaymentMethod === 'apple-pay' && (
                      <div className="d-flex justify-content-center align-items-center">
                        <small className="text-muted me-2">Ready to pay with:</small>
                        <div className="d-flex gap-2">
                          <img
                            src="/apple-pay.png"
                            alt="Apple Pay"
                            style={{
                              height: "24px",
                              width: "auto",
                              objectFit: "contain"
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Single payment form for all card types */}
                <div className="payment-widget-wrapper" style={{
                  opacity: widgetLoading ? 0.3 : 1,
                  transition: 'opacity 0.5s ease',
                  marginBottom: '20px',
                  transform: widgetLoading ? 'translateY(10px)' : 'translateY(0)',
                  filter: widgetLoading ? 'blur(1px)' : 'none'
                }}>
                  {/* HyperPay will inject payment fields here dynamically */}
                </div>

                {/* Loading indicator while form initializes */}
                {loading && !widgetLoading && (
                  <div className="text-center mb-3">
                    <div className="spinner-border spinner-border-sm" style={{ color: "#040c4b" }} role="status">
                      <span className="visually-hidden">Processing...</span>
                    </div>
                    <small className="d-block mt-2 text-muted">Processing your payment...</small>
                  </div>
                )}

                {/* Back button to return to payment details */}
                {!widgetLoading && (
                  <div className="d-flex justify-content-center align-items-center mt-4">
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center"
                      style={{
                        borderRadius: "12px",
                        padding: "12px 20px",
                        border: "2px solid #e5e7eb",
                        color: "#6b7280",
                        backgroundColor: "transparent",
                        fontSize: "14px",
                        fontWeight: "500",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = "#cbd5e1";
                        e.target.style.backgroundColor = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.backgroundColor = "transparent";
                      }}
                      onClick={() => {
                        setShowPaymentForm(false);
                        setShowPaymentMethodSelection(false);
                        setCheckoutId(null);
                        setWidgetLoading(false);
                        setSelectedPaymentMethod(null);
                        // Clean up HyperPay scripts
                        const existingScript = document.querySelector('script[src*="paymentWidgets.js"]');
                        if (existingScript) {
                          existingScript.remove();
                        }
                        const existingOptionsScript = document.querySelector('script[data-wpwl-options]');
                        if (existingOptionsScript) {
                          existingOptionsScript.remove();
                        }
                        // Remove custom CSS
                        const customCSS = document.querySelector('style[data-hyperpay-custom]');
                        if (customCSS) {
                          customCSS.remove();
                        }
                      }}
                    >
                      <span style={{ marginRight: "8px" }}>←</span>
                      Back to Details
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-muted py-3">
          © 2025 Al Hilal Academy. All rights reserved.
        </div>
      </div>
    </>
  );
};

export default PaymentPreview;